import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { getFunctions } from "firebase/functions";
import { httpsCallable } from "../../../shared/legacyData/legacyCallable";
import {
  getFirestore,
  doc,
  getDoc,
  collection,
  getDocs,
} from "firebase/firestore";
import "./LearnerUnitProgress.css";

const COMPANY_CODE = "TPC_Group";
const COLLECTION_BRAND = "TechPerformanceCentre";

// A lightweight page that lists each active intake block (course/program)
// the learner is enrolled in and shows the learner's current unit based on
// Unity progress.

export default function LearnerUnitProgress() {
  // We no longer depend on a specific learner; this page shows all learners per block

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [intakeBlocks, setIntakeBlocks] = useState([]);

  const [progressLoading, setProgressLoading] = useState(false);
  // Map of blockId -> array of { learnerEmail, currentUnitName, status }
  const [learnerProgressByBlock, setLearnerProgressByBlock] = useState({});
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalAssessments, setModalAssessments] = useState([]);
  const [savingGrades, setSavingGrades] = useState(false);
  const [expandedAssessments, setExpandedAssessments] = useState(new Set());
  const [expandAll, setExpandAll] = useState(false);

  // Collapsible and pagination state
  const [collapsedBlocks, setCollapsedBlocks] = useState(new Set());
  const [blockPages, setBlockPages] = useState({});
  const [modalComponentProgress, setModalComponentProgress] = useState(null);
  const [selectedComponentKey, setSelectedComponentKey] = useState(null);
  const [selectedUnitName, setSelectedUnitName] = useState(null);
  const [unitOutcomesData, setUnitOutcomesData] = useState(null);
  const [loadingUnitData, setLoadingUnitData] = useState(false);
  const [expandedOutcomes, setExpandedOutcomes] = useState(new Set());
  const [expandedOutlines, setExpandedOutlines] = useState(new Set());
  const [currentProgramName, setCurrentProgramName] = useState(null);
  const [currentBlockKey, setCurrentBlockKey] = useState(null);
  const [fetchedAssessmentDetails, setFetchedAssessmentDetails] = useState({});
  const [loadingAssessmentDetails, setLoadingAssessmentDetails] = useState({});

  const functions = useMemo(() => getFunctions(), []);
  const updateSystemDocument = useMemo(
    () => httpsCallable(functions, "updateSystemDocument"),
    [functions]
  );

  // 1) Fetch all intake blocks
  useEffect(() => {
    const fetchIntakeBlocks = async () => {
      try {
        setLoading(true);
        setError(null);
        const getSystemDocuments = httpsCallable(
          functions,
          "getSystemDocuments"
        );

        const result = await getSystemDocuments({
          company: "TPC_Group",
          collectionBrand: "TechPerformanceCentre",
          systemName: "intakeBlocks",
          auth: true,
        });

        if (result?.data?.success && Array.isArray(result?.data?.data)) {
          const blocks = result.data.data.filter(
            (block) =>
              Array.isArray(block?.learnerIds) &&
              block.learnerIds.length > 0 &&
              block.status === "Full"
          );
          setIntakeBlocks(blocks);
        } else {
          setIntakeBlocks([]);
        }
      } catch (err) {
        console.error("Error fetching intake blocks:", err);
        setError("Failed to load intake blocks");
        setIntakeBlocks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchIntakeBlocks();
  }, [functions]);

  // 2) For each block, compute each learner's current unit by calling Unity progress
  useEffect(() => {
    const fetchProgressForBlocks = async () => {
      if (intakeBlocks.length === 0) {
        setLearnerProgressByBlock({});
        return;
      }

      try {
        setProgressLoading(true);
        const getProgramDetails = httpsCallable(functions, "getProgramDetails");
        const getLearnerProgress = httpsCallable(
          functions,
          "getLearnerProgress"
        );
        const getSystemDocuments = httpsCallable(
          functions,
          "getSystemDocuments"
        );

        // Build per-block learner work items
        const perBlockResults = {};

        // Preload all learnerAssessmentStory docs once and index by email
        let assessmentStoriesByEmail = {};
        try {
          const storiesRes = await getSystemDocuments({
            company: "TPC_Group",
            collectionBrand: "TechPerformanceCentre",
            systemName: "learnerAssessmentStory",
          });
          if (
            storiesRes?.data?.success &&
            Array.isArray(storiesRes?.data?.data)
          ) {
            assessmentStoriesByEmail = Object.fromEntries(
              storiesRes.data.data.map((doc) => [doc.id, doc])
            );
          }
        } catch (_e) {}
        // helper for modal access
        setStoryIndex(assessmentStoriesByEmail);

        for (const block of intakeBlocks) {
          const schedule = Array.isArray(block?.programSchedule)
            ? block.programSchedule
            : [];
          const unitNames = schedule
            .map((item) => item?.unitTitle || item?.title || item?.name)
            .filter(Boolean);

          const programName =
            block?.programName ||
            block?.program ||
            block?.programme ||
            block?.name ||
            "TPC_Program";
          const blockId = block?.id || programName;

          const learnerIds = Array.isArray(block?.learnerIds)
            ? block.learnerIds
            : [];

          // Fetch full program structure once for this block
          let programData = null;
          try {
            const res = await getProgramDetails({
              programId: programName,
              company: "TPC_Group",
            });
            if (res?.data?.success) {
              programData = res.data.data;
            }
          } catch (_e) {}

          if (!programData) {
            // If we cannot fetch program structure, provide minimal rows
            perBlockResults[blockId] = {
              blockLabel: programName,
              learners: learnerIds.map((learnerEmail) => ({
                learnerEmail,
                currentUnitName: "—",
                unitsCompleted: 0,
                unitsTotal: unitNames.length || 0,
                status: unitNames.length > 0 ? "Not Started" : "—",
              })),
            };
            continue;
          }

          // Build an ordered list of units with their outcomes from programData
          const orderedUnits = buildOrderedUnitsFromProgram(programData);
          // Build fast lookup: normalized outcome title -> { unitName, outcomeName }
          const outcomeToUnit = buildOutcomeToUnitMap(programData);
          const unitsTotal = orderedUnits.length;

          // For each learner in the block, try assessment story first, then fall back to lock walk
          const learnerRows = await Promise.all(
            learnerIds.map(async (learnerEmail) => {
              let currentUnitName = "—";
              let completedUnitsCount = 0;
              let usedStory = false;
              let progressText = null;

              // A) Use learnerAssessmentStory: pick the most recent completed assessment and map to its outcome/unit
              const storyDoc =
                assessmentStoriesByEmail[learnerEmail]?.data ||
                assessmentStoriesByEmail[learnerEmail] ||
                null;
              const assessmentsCount =
                countCompletedAssessmentsFromStory(storyDoc);
              const averagePercent = computeAveragePercentFromStory(storyDoc);
              const mappedFromStory = mapLatestMatchingStoryOutcomeToUnit(
                storyDoc,
                outcomeToUnit,
                orderedUnits
              );
              if (mappedFromStory) {
                const { unitName, outcomeName, sourceTitle } = mappedFromStory;
                const unitIndex = orderedUnits.findIndex(
                  (u) => u.name === unitName
                );
                if (unitIndex >= 0) {
                  currentUnitName = unitName;
                  completedUnitsCount = unitIndex; // units before this index considered completed
                  usedStory = true;
                  progressText = `${unitName} — ${outcomeName || sourceTitle}`;
                }
              }

              // B) Fallback to module progress lock-walk if story missing or inconclusive
              if (!usedStory) {
                const perModuleProgress = {};
                for (const module of programData.modules || []) {
                  try {
                    const resp = await getLearnerProgress({
                      learnerEmail: learnerEmail,
                      programName: programData.name || programName,
                      moduleName: module.name,
                    });
                    if (resp?.data?.success) {
                      perModuleProgress[module.name] = resp.data.progress || {};
                    } else {
                      perModuleProgress[module.name] = {};
                    }
                  } catch (_e) {
                    perModuleProgress[module.name] = {};
                  }
                }

                for (const unit of orderedUnits) {
                  const outcomeIds = unit.outcomes.map(
                    (o) =>
                      `${o.name}|||${
                        programData.name || programName
                      }|||TPC_Group`
                  );
                  const allDone =
                    outcomeIds.length === 0
                      ? false
                      : outcomeIds.every((oid) =>
                          findOutcomeCompletedInModules(oid, perModuleProgress)
                        );
                  if (allDone) {
                    completedUnitsCount += 1;
                    continue;
                  } else {
                    currentUnitName = unit.name;
                    if (!progressText) progressText = `${unit.name}`;
                    break;
                  }
                }
              }

              if (completedUnitsCount === unitsTotal && unitsTotal > 0) {
                currentUnitName =
                  orderedUnits[unitsTotal - 1]?.name || currentUnitName;
              }

              const status =
                unitsTotal === 0
                  ? "Not Started"
                  : completedUnitsCount === 0
                  ? "Not Started"
                  : completedUnitsCount >= unitsTotal
                  ? "Completed"
                  : "In Progress";

              const componentProgress = buildComponentProgressTree(
                programData,
                storyDoc
              );

              return {
                learnerEmail,
                currentUnitName,
                unitsCompleted: completedUnitsCount,
                unitsTotal: unitsTotal,
                assessmentsCount,
                averagePercent,
                progressText:
                  progressText ||
                  (currentUnitName ? `${currentUnitName}` : "—"),
                status,
                componentProgress,
              };
            })
          );

          perBlockResults[blockId] = {
            blockLabel: programName,
            learners: learnerRows,
            programStructure: sanitizeProgramStructure(programData),
          };
        }

        setLearnerProgressByBlock(perBlockResults);
      } catch (err) {
        console.error("Error computing learner progress:", err);
        setLearnerProgressByBlock({});
      } finally {
        setProgressLoading(false);
      }
    };

    fetchProgressForBlocks();
  }, [functions, intakeBlocks]);

  useEffect(() => {
    if (!showModal) {
      setModalComponentProgress(null);
      setSelectedComponentKey(null);
      setSelectedUnitName(null);
      setExpandedAssessments(new Set());
      setExpandAll(false);
      setUnitOutcomesData(null);
      setExpandedOutcomes(new Set());
      setExpandedOutlines(new Set());
      setCurrentProgramName(null);
      setCurrentBlockKey(null);
    }
  }, [showModal]);

  // Fetch unit outcomes and outlines when a unit is selected
  useEffect(() => {
    const fetchUnitOutcomesAndOutlines = async () => {
      if (!selectedUnitName || !modalComponentProgress) {
        setUnitOutcomesData(null);
        return;
      }

      // First, try to get unit data from program structure (already loaded)
      if (currentBlockKey && learnerProgressByBlock[currentBlockKey]) {
        const blockGroup = learnerProgressByBlock[currentBlockKey];
        const programStructure = blockGroup?.programStructure;

        if (programStructure && Array.isArray(programStructure.units)) {
          const unitData = programStructure.units.find(
            (u) => normalizeTitle(u.name) === normalizeTitle(selectedUnitName)
          );

          if (unitData && Array.isArray(unitData.outcomes)) {
            // We have unit data with outcomes from program structure
            setUnitOutcomesData({
              unit: { name: selectedUnitName },
              outcomes: unitData.outcomes.map((outcome) => ({
                outcomeData:
                  typeof outcome === "string"
                    ? { name: outcome }
                    : typeof outcome === "object" && outcome.name
                    ? outcome
                    : { name: String(outcome) },
                outline: { outlineNotCreated: true },
              })),
            });
            setLoadingUnitData(false);
            return;
          }
        }
      }

      // Get program name from stored value or try to extract from structure
      let programName = currentProgramName;
      if (!programName && modalComponentProgress?.components?.length > 0) {
        const firstComponent = modalComponentProgress.components[0];
        if (firstComponent?.moduleName) {
          programName = firstComponent.moduleName.split("|||")[0];
        }
      }

      // If we don't have a valid program name, skip API call and use fallback
      if (!programName || programName === "TPC_Program") {
        setLoadingUnitData(false);
        // Fallback: try to get outcomes from component structure
        const unitFromProgram = findUnitInProgramStructure(
          selectedUnitName,
          modalComponentProgress
        );
        if (unitFromProgram && unitFromProgram.outcomes) {
          setUnitOutcomesData({
            unit: { name: selectedUnitName },
            outcomes: (unitFromProgram.outcomes || []).map((outcome) => ({
              outcomeData:
                typeof outcome === "string" ? { name: outcome } : outcome,
              outline: { outlineNotCreated: true },
            })),
          });
        } else {
          setUnitOutcomesData(null);
        }
        return;
      }

      try {
        setLoadingUnitData(true);
        const fetchUnitData = httpsCallable(
          functions,
          "fetchUnitDataForAIContentBuilderV2"
        );

        const result = await fetchUnitData({
          programId: programName,
          unitCode: selectedUnitName,
          company: COMPANY_CODE,
          collectionBrand: COLLECTION_BRAND,
        });

        if (result?.data?.success && result?.data?.data) {
          setUnitOutcomesData(result.data.data);
        } else {
          // Fallback: try to get outcomes from program structure
          const unitFromProgram = findUnitInProgramStructure(
            selectedUnitName,
            modalComponentProgress
          );
          if (unitFromProgram) {
            setUnitOutcomesData({
              unit: { name: selectedUnitName },
              outcomes: (unitFromProgram.outcomes || []).map((outcome) => ({
                outcomeData:
                  typeof outcome === "string" ? { name: outcome } : outcome,
                outline: { outlineNotCreated: true },
              })),
            });
          } else {
            setUnitOutcomesData(null);
          }
        }
      } catch (err) {
        console.error("Error fetching unit outcomes:", err);
        // Fallback: try to get outcomes from program structure
        const unitFromProgram = findUnitInProgramStructure(
          selectedUnitName,
          modalComponentProgress
        );
        if (unitFromProgram) {
          setUnitOutcomesData({
            unit: { name: selectedUnitName },
            outcomes: (unitFromProgram.outcomes || []).map((outcome) => ({
              outcomeData:
                typeof outcome === "string" ? { name: outcome } : outcome,
              outline: { outlineNotCreated: true },
            })),
          });
        } else {
          setUnitOutcomesData(null);
        }
      } finally {
        setLoadingUnitData(false);
      }
    };

    fetchUnitOutcomesAndOutlines();
  }, [
    selectedUnitName,
    modalComponentProgress,
    functions,
    currentProgramName,
    currentBlockKey,
    learnerProgressByBlock,
  ]);

  const [storyIndex, setStoryIndex] = useState({});

  function handleOpenAssessmentsModal(
    blockKey,
    learnerEmail,
    componentKey,
    unitName
  ) {
    const story =
      storyIndex[learnerEmail]?.data || storyIndex[learnerEmail] || null;
    const arr = extractAssessmentsArrayFromStory(story);

    console.log("[LearnerUnitProgress] Opening assessments for:", learnerEmail);
    console.log("[LearnerUnitProgress] Story data:", story);
    console.log("[LearnerUnitProgress] Extracted assessments array:", arr);
    console.log("[LearnerUnitProgress] First assessment sample:", arr[0]);

    const blockGroup = learnerProgressByBlock[blockKey];
    const learnerRow = blockGroup?.learners?.find(
      (row) => row.learnerEmail === learnerEmail
    );
    const componentProgress = learnerRow?.componentProgress || null;
    const availableComponents = componentProgress?.components || [];
    const selectedComponent =
      availableComponents.find((comp) => comp.componentKey === componentKey) ||
      availableComponents[0] ||
      null;

    // Limit modal view to the module that owns the selected component
    const moduleNameForSelection = selectedComponent?.moduleName || null;
    const filteredComponents =
      moduleNameForSelection && availableComponents.length > 0
        ? availableComponents.filter(
            (comp) => comp.moduleName === moduleNameForSelection
          )
        : availableComponents;
    const selectedUnit =
      selectedComponent?.units?.find((unit) => unit.unitName === unitName) ||
      selectedComponent?.units?.[0] ||
      null;

    setSelectedLearner(learnerEmail);
    setModalAssessments(arr);
    setModalComponentProgress(
      componentProgress
        ? { ...componentProgress, components: filteredComponents }
        : null
    );
    setSelectedComponentKey(selectedComponent?.componentKey || null);
    setSelectedUnitName(selectedUnit?.unitName || null);
    // Store the program name and block key from the block
    setCurrentProgramName(blockGroup?.blockLabel || null);
    setCurrentBlockKey(blockKey);
    setShowModal(true);
    setExpandedAssessments(new Set());
    setExpandAll(false);
  }

  function handleEditAssessmentPercent(idx, value) {
    setModalAssessments((prev) => {
      const next = prev.slice();
      const target = { ...next[idx] };
      if (!target.assessment) target.assessment = {};
      target.assessment.percent = isFinite(value)
        ? Math.max(0, Math.min(100, value))
        : 0;
      next[idx] = target;
      return next;
    });
  }

  async function handleSaveManualGrades() {
    if (!selectedLearner) return;
    try {
      setSavingGrades(true);
      // Write back to learnerAssessmentStory/{email}
      // Shape: { complexityHistory: [{ sectionTitle, assessment: { percent, ... }, ... }, ...] }
      const newHistory = modalAssessments.map((a) => ({
        sectionTitle: a.sectionTitle,
        assessment: {
          ...(a.assessment || {}),
          percent: a.assessment?.percent ?? a.percent ?? 0,
        },
      }));
      await updateSystemDocument({
        company: "TPC_Group",
        collectionBrand: "TechPerformanceCentre",
        systemName: "learnerAssessmentStory",
        uniqueId: selectedLearner,
        data: { complexityHistory: newHistory },
      });

      // Update averages in UI
      const avg = computeAveragePercentFromStory({
        complexityHistory: newHistory,
      });
      let updatedModalComponentProgress = null;
      setLearnerProgressByBlock((prev) => {
        const copy = { ...prev };
        Object.keys(copy).forEach((blockId) => {
          const programStructure = copy[blockId]?.programStructure;
          copy[blockId] = {
            ...copy[blockId],
            learners: (copy[blockId]?.learners || []).map((row) => {
              if (row.learnerEmail !== selectedLearner) return row;
              const updatedComponentProgress = buildComponentProgressTree(
                programStructure,
                { complexityHistory: newHistory }
              );
              if (!updatedModalComponentProgress) {
                updatedModalComponentProgress = updatedComponentProgress;
              }
              return {
                ...row,
                averagePercent: avg,
                componentProgress: updatedComponentProgress,
              };
            }),
          };
        });
        return copy;
      });
      if (updatedModalComponentProgress) {
        setModalComponentProgress(updatedModalComponentProgress);
      }

      setShowModal(false);
    } catch (e) {
      console.error("Failed saving grades:", e);
    } finally {
      setSavingGrades(false);
    }
  }

  async function toggleExpandAssessment(
    idx,
    outcomeName = null,
    outlineTitle = null
  ) {
    const isCurrentlyExpanded = expandedAssessments.has(idx);

    setExpandedAssessments((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });

    // If expanding and we don't have details yet, fetch them
    if (!isCurrentlyExpanded && selectedLearner && selectedUnitName) {
      // Build unitId
      const unitId = buildUnitDocumentId(selectedUnitName, currentProgramName);

      // Get the assessment entry to check for stored IDs
      const assessmentEntry = modalAssessments[idx];

      // Try to get outcomeId and outlineId from context
      let outcomeId = null;
      let outlineId = null;

      // First, try to get IDs from the assessment entry itself
      if (assessmentEntry) {
        // Get sectionTitle from entry - this is the actual assessment title
        const sectionTitle =
          assessmentEntry.sectionTitle || assessmentEntry.entry?.sectionTitle;

        outcomeId =
          assessmentEntry.outcomeId ||
          assessmentEntry.entry?.outcomeId ||
          assessmentEntry.outcomeName ||
          assessmentEntry.entry?.outcomeName;

        // Prioritize sectionTitle as outlineId since that's what's stored in Firestore
        outlineId =
          assessmentEntry.sectionId ||
          assessmentEntry.outlineId ||
          assessmentEntry.entry?.sectionId ||
          assessmentEntry.entry?.outlineId ||
          sectionTitle; // Use sectionTitle as outlineId

        console.log("[toggleExpandAssessment] Assessment entry data:", {
          sectionTitle,
          outcomeId:
            assessmentEntry.outcomeId || assessmentEntry.entry?.outcomeId,
          outlineIdFromEntry:
            assessmentEntry.sectionId || assessmentEntry.outlineId,
          fullEntry: assessmentEntry,
        });
      }

      // If we have unitOutcomesData, try to find the matching outcome and outline
      if (!outcomeId && unitOutcomesData?.outcomes && outcomeName) {
        const outcome = unitOutcomesData.outcomes.find(
          (o) => (o.outcomeData?.name || o.name) === outcomeName
        );
        if (outcome) {
          // Try outcome ID from outcomeData
          outcomeId =
            outcome.outcomeData?.id ||
            outcome.outcomeData?.outcomeId ||
            outcome.id ||
            outcome.outcomeId;

          // Try to find the outline/section
          if (!outlineId && outlineTitle && outcome.outline?.sections) {
            const section = outcome.outline.sections.find(
              (s) => (s.title || s.sectionId) === outlineTitle
            );
            if (section) {
              outlineId = section.sectionId || section.id || section.outlineId;
            }
          }
          // If no outlineId found, try to use the first section or outlineTitle as fallback
          if (!outlineId) {
            outlineId =
              outcome.outline?.sections?.[0]?.sectionId ||
              outcome.outline?.sections?.[0]?.id ||
              outcome.outline?.sections?.[0]?.outlineId;
          }
        }
      }

      // If we still don't have outlineId, try to get it from assessment entry's sectionTitle
      if (!outlineId && assessmentEntry) {
        const sectionTitle =
          assessmentEntry.sectionTitle || assessmentEntry.entry?.sectionTitle;
        if (sectionTitle) {
          outlineId = sectionTitle;
        }
      }

      // Fallback: normalize names to IDs (remove special chars, lowercase, etc.)
      if (!outcomeId && outcomeName) {
        // Try normalizing the outcome name to an ID format
        outcomeId = outcomeName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "");
      }

      // Use outlineTitle or sectionTitle as fallback for outlineId
      if (!outlineId) {
        const fallbackTitle =
          outlineTitle ||
          assessmentEntry?.sectionTitle ||
          assessmentEntry?.entry?.sectionTitle;
        if (fallbackTitle) {
          outlineId = fallbackTitle;
        }
      }

      // Final fallback - but avoid "other" or "default" if we have any title
      if (!outcomeId) {
        outcomeId = outcomeName || "default";
      }
      if (!outlineId) {
        // Only use "default" if we truly have nothing
        outlineId =
          outlineTitle ||
          assessmentEntry?.sectionTitle ||
          assessmentEntry?.entry?.sectionTitle ||
          "default";
      }

      // Normalize outlineId to match Firestore document ID format (lowercase, dashes, etc.)
      // But keep original for first attempt, then try normalized versions

      console.log("[toggleExpandAssessment] Extracted IDs:", {
        unitId,
        outcomeId,
        outlineId,
        outcomeName,
        outlineTitle,
        assessmentEntry: assessmentEntry ? Object.keys(assessmentEntry) : null,
      });

      // Check if we already have this data cached
      const cacheKey = `${selectedLearner}|||${unitId}|||${outcomeId}|||${outlineId}`;
      if (
        !fetchedAssessmentDetails[cacheKey] &&
        !loadingAssessmentDetails[cacheKey]
      ) {
        setLoadingAssessmentDetails((prev) => ({ ...prev, [cacheKey]: true }));

        try {
          const details = await fetchAssessmentDetails(
            selectedLearner,
            unitId,
            outcomeId,
            outlineId
          );

          setFetchedAssessmentDetails((prev) => ({
            ...prev,
            [cacheKey]: details,
          }));
        } catch (error) {
          console.error("Error fetching assessment details:", error);
          setFetchedAssessmentDetails((prev) => ({
            ...prev,
            [cacheKey]: null, // Cache null to prevent retries
          }));
        } finally {
          setLoadingAssessmentDetails((prev) => {
            const next = { ...prev };
            delete next[cacheKey];
            return next;
          });
        }
      }
    }
  }

  const handleExpandAll = () => {
    if (expandAll) {
      // Collapse all
      setExpandedAssessments(new Set());
      setExpandAll(false);
    } else {
      // Expand all
      const allIndices = new Set(modalAssessments.map((_, idx) => idx));
      setExpandedAssessments(allIndices);
      setExpandAll(true);
    }
  };

  const handleSelectComponent = (componentKey) => {
    if (
      !modalComponentProgress ||
      !Array.isArray(modalComponentProgress.components)
    ) {
      return;
    }
    const match = modalComponentProgress.components.find(
      (component) => component.componentKey === componentKey
    );
    if (!match) return;
    setSelectedComponentKey(componentKey);
    const firstUnit = match.units?.[0] || null;
    setSelectedUnitName(firstUnit?.unitName || null);
    setExpandedAssessments(new Set());
    setExpandAll(false);
  };

  const handleSelectUnit = (unitName) => {
    setSelectedUnitName(unitName);
    setExpandedAssessments(new Set());
    setExpandAll(false);
    // Auto-expand "Uncategorized" outcome and its outline if it exists, so assessments are visible
    setExpandedOutcomes(new Set(["Uncategorized"]));
    setExpandedOutlines(new Set(["Uncategorized|||Other"]));
  };

  const toggleOutcome = (outcomeName) => {
    setExpandedOutcomes((prev) => {
      const next = new Set(prev);
      if (next.has(outcomeName)) {
        next.delete(outcomeName);
      } else {
        next.add(outcomeName);
      }
      return next;
    });
  };

  const toggleOutline = (outcomeName, outlineTitle) => {
    const key = `${outcomeName}|||${outlineTitle}`;
    setExpandedOutlines((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Calculate total average across all learners
  const calculateTotalAverage = () => {
    let totalPercent = 0;
    let count = 0;

    intakeBlocks.forEach((block) => {
      const blockKey =
        block?.id ||
        block?.programName ||
        block?.program ||
        block?.name ||
        "Unknown";
      const grouped = learnerProgressByBlock[blockKey];

      (grouped?.learners || []).forEach((row) => {
        if (typeof row.averagePercent === "number") {
          totalPercent += row.averagePercent;
          count += 1;
        }
      });
    });

    return count > 0 ? (totalPercent / count).toFixed(1) : "0.0";
  };

  // Calculate average for a specific block
  const calculateBlockAverage = (blockKey) => {
    const grouped = learnerProgressByBlock[blockKey];
    let totalPercent = 0;
    let count = 0;

    (grouped?.learners || []).forEach((row) => {
      if (typeof row.averagePercent === "number") {
        totalPercent += row.averagePercent;
        count += 1;
      }
    });

    return count > 0 ? (totalPercent / count).toFixed(1) : "0.0";
  };

  // Toggle block collapse
  const toggleBlockCollapse = (blockKey) => {
    setCollapsedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(blockKey)) {
        next.delete(blockKey);
      } else {
        next.add(blockKey);
      }
      return next;
    });
  };

  // Pagination helpers
  const ITEMS_PER_PAGE = 20;

  const getCurrentPage = (blockKey) => {
    return blockPages[blockKey] || 1;
  };

  const setCurrentPage = (blockKey, page) => {
    setBlockPages((prev) => ({ ...prev, [blockKey]: page }));
  };

  const getPaginatedLearners = (learners, blockKey) => {
    const currentPage = getCurrentPage(blockKey);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return learners.slice(startIndex, endIndex);
  };

  const getTotalPages = (learners) => {
    return Math.ceil(learners.length / ITEMS_PER_PAGE);
  };

  const totalAverage = calculateTotalAverage();

  // Calculate component-level average (average of all assessments in all units of that component)
  const calculateComponentAverage = useCallback((component) => {
    if (!component || !Array.isArray(component.units)) return null;
    let totalPercent = 0;
    let count = 0;

    console.log(
      `[calculateComponentAverage] Starting for ${component.componentName}`
    );
    console.log(`[calculateComponentAverage] Component structure:`, component);

    component.units.forEach((unit) => {
      console.log(
        `[calculateComponentAverage] Processing unit: ${unit.unitName}`
      );
      console.log(
        `[calculateComponentAverage] Unit assessments array:`,
        unit.assessments
      );

      if (Array.isArray(unit.assessments)) {
        unit.assessments.forEach((assessment, idx) => {
          console.log(
            `[calculateComponentAverage] Assessment ${idx}:`,
            assessment
          );
          console.log(
            `[calculateComponentAverage] Assessment keys:`,
            Object.keys(assessment || {})
          );
          console.log(
            `[calculateComponentAverage] assessment.percent:`,
            assessment?.percent
          );
          console.log(
            `[calculateComponentAverage] assessment.assessment:`,
            assessment?.assessment
          );
          console.log(
            `[calculateComponentAverage] assessment.assessment?.percent:`,
            assessment?.assessment?.percent
          );

          const percent =
            typeof assessment.percent === "number"
              ? assessment.percent
              : typeof assessment.assessment?.percent === "number"
              ? assessment.assessment.percent
              : null;

          console.log(
            `[calculateComponentAverage] Extracted percent:`,
            percent
          );

          if (percent !== null && !isNaN(percent)) {
            totalPercent += percent;
            count += 1;
          }
        });
      }
    });

    const average = count > 0 ? (totalPercent / count).toFixed(1) : null;
    console.log(`[calculateComponentAverage] ${component.componentName}:`, {
      totalPercent,
      count,
      average,
      unitsCount: component.units.length,
      assessmentsPerUnit: component.units.map((u) => ({
        name: u.unitName,
        count: u.assessments?.length || 0,
        sampleAssessment: u.assessments?.[0] || null,
      })),
    });
    return average;
  }, []);

  // Calculate unit-level average (average of all assessments in that unit)
  const calculateUnitAverage = useCallback((unit) => {
    if (!unit || !Array.isArray(unit.assessments)) return null;
    let totalPercent = 0;
    let count = 0;

    console.log(`[calculateUnitAverage] Starting for ${unit.unitName}`);
    console.log(`[calculateUnitAverage] Unit assessments:`, unit.assessments);
    console.log(
      `[calculateUnitAverage] Assessments count:`,
      unit.assessments.length
    );

    unit.assessments.forEach((assessment, idx) => {
      console.log(`[calculateUnitAverage] Assessment ${idx}:`, assessment);
      console.log(
        `[calculateUnitAverage] Assessment ${idx} keys:`,
        Object.keys(assessment || {})
      );
      console.log(
        `[calculateUnitAverage] Assessment ${idx} percent:`,
        assessment?.percent
      );
      console.log(
        `[calculateUnitAverage] Assessment ${idx} assessment object:`,
        assessment?.assessment
      );

      const percent =
        typeof assessment.percent === "number"
          ? assessment.percent
          : typeof assessment.assessment?.percent === "number"
          ? assessment.assessment.percent
          : null;

      console.log(
        `[calculateUnitAverage] Assessment ${idx} extracted percent:`,
        percent
      );

      if (percent !== null && !isNaN(percent)) {
        totalPercent += percent;
        count += 1;
      }
    });

    const average = count > 0 ? (totalPercent / count).toFixed(1) : null;
    console.log(`[calculateUnitAverage] ${unit.unitName} FINAL:`, {
      totalPercent,
      count,
      average,
      assessmentPercents: unit.assessments.map((a) => ({
        percent: a.percent,
        hasAssessment: !!a.assessment,
        assessmentPercent: a.assessment?.percent,
      })),
    });
    return average;
  }, []);

  const selectedComponent = useMemo(() => {
    if (
      !modalComponentProgress ||
      !Array.isArray(modalComponentProgress.components)
    ) {
      return null;
    }
    if (selectedComponentKey) {
      const match = modalComponentProgress.components.find(
        (component) => component.componentKey === selectedComponentKey
      );
      if (match) return match;
    }
    return modalComponentProgress.components[0] || null;
  }, [modalComponentProgress, selectedComponentKey]);

  const selectedUnit = useMemo(() => {
    if (!selectedComponent || !Array.isArray(selectedComponent.units))
      return null;
    if (selectedUnitName) {
      const match = selectedComponent.units.find(
        (unit) => unit.unitName === selectedUnitName
      );
      if (match) return match;
    }
    return selectedComponent.units[0] || null;
  }, [selectedComponent, selectedUnitName]);

  // Group assessments by outcome and outline
  const groupedAssessmentsByOutcome = useMemo(() => {
    if (!selectedUnit) return {};

    // Always show outcomes even if unitOutcomesData is not loaded yet
    const outcomes = unitOutcomesData?.outcomes || [];

    // If no outcomes data, try to get from program structure
    if (
      outcomes.length === 0 &&
      currentBlockKey &&
      learnerProgressByBlock[currentBlockKey]
    ) {
      const blockGroup = learnerProgressByBlock[currentBlockKey];
      const programStructure = blockGroup?.programStructure;
      if (programStructure && Array.isArray(programStructure.units)) {
        const unitData = programStructure.units.find(
          (u) => normalizeTitle(u.name) === normalizeTitle(selectedUnitName)
        );
        if (unitData && Array.isArray(unitData.outcomes)) {
          unitData.outcomes.forEach((outcome) => {
            const outcomeName =
              typeof outcome === "string" ? outcome : outcome?.name || "";
            if (
              outcomeName &&
              !outcomes.find(
                (o) => (o.outcomeData?.name || o.name) === outcomeName
              )
            ) {
              outcomes.push({
                outcomeData:
                  typeof outcome === "string" ? { name: outcome } : outcome,
                outline: { outlineNotCreated: true },
              });
            }
          });
        }
      }
    }

    const unitAssessments = Array.isArray(selectedUnit.assessments)
      ? selectedUnit.assessments
      : [];

    const grouped = {};

    // If no outcomes but we have assessments, create an "Uncategorized" group to show them
    if (outcomes.length === 0 && unitAssessments.length > 0) {
      grouped["Uncategorized"] = {
        outcome: { outcomeData: { name: "Uncategorized" } },
        outlines: {
          Other: {
            section: { title: "Other", sectionId: "other" },
            assessments: [],
          },
        },
      };
    }

    // Always create outcome groups, even if no assessments
    outcomes.forEach((outcome) => {
      const outcomeName = outcome.outcomeData?.name || outcome.name || "";
      if (!outcomeName) return;

      grouped[outcomeName] = {
        outcome,
        outlines: {},
      };

      // Get outline sections
      const outline = outcome.outline;
      const sections = outline?.sections || [];

      // If no sections but outline exists, create a default section
      if (sections.length === 0 && outline && !outline.outlineNotCreated) {
        grouped[outcomeName].outlines["Default"] = {
          section: { title: "Default", sectionId: "default" },
          assessments: [],
        };
      } else {
        sections.forEach((section) => {
          const sectionTitle = section.title || section.sectionId || "";
          if (sectionTitle) {
            grouped[outcomeName].outlines[sectionTitle] = {
              section,
              assessments: [],
            };
          }
        });
      }

      // If no outlines at all, create an "Other" category
      if (Object.keys(grouped[outcomeName].outlines).length === 0) {
        grouped[outcomeName].outlines["Other"] = {
          section: { title: "Other", sectionId: "other" },
          assessments: [],
        };
      }
    });

    // Map assessments to outcomes and outlines
    unitAssessments.forEach((detail, localIdx) => {
      const historyIndex =
        typeof detail.historyIndex === "number" ? detail.historyIndex : null;
      const baseEntry =
        historyIndex !== null && modalAssessments[historyIndex]
          ? modalAssessments[historyIndex]
          : null;

      const fallbackEntry = {
        sectionTitle:
          detail.sectionTitle ||
          detail.outcomeName ||
          `Assessment ${localIdx + 1}`,
        assessment: {
          percent: typeof detail.percent === "number" ? detail.percent : null,
          score: typeof detail.score === "number" ? detail.score : null,
          maxScore:
            typeof detail.maxScore === "number" ? detail.maxScore : null,
        },
      };

      const entry = baseEntry || fallbackEntry;
      const assessmentData = {
        entry,
        idx: historyIndex !== null ? historyIndex : localIdx + 1000,
      };

      // Try to match assessment to outcome and outline
      const sectionTitle = entry.sectionTitle || "";
      const normalizedSectionTitle = normalizeTitle(sectionTitle);

      // Find matching outcome and outline
      let matchedOutcome = null;
      let matchedOutline = null;

      // First, try to match by outline section title (more specific)
      for (const [outcomeName, outcomeGroup] of Object.entries(grouped)) {
        for (const [outlineTitle, outlineGroup] of Object.entries(
          outcomeGroup.outlines
        )) {
          const normalizedOutlineTitle = normalizeTitle(outlineTitle);
          // Check if sectionTitle matches outline section
          if (
            normalizedSectionTitle.includes(normalizedOutlineTitle) ||
            normalizedOutlineTitle.includes(normalizedSectionTitle) ||
            sectionTitle.toLowerCase().includes(outlineTitle.toLowerCase()) ||
            outlineTitle.toLowerCase().includes(sectionTitle.toLowerCase())
          ) {
            matchedOutcome = outcomeName;
            matchedOutline = outlineTitle;
            break;
          }
        }
        if (matchedOutcome) break;
      }

      // If no outline match, try matching by outcome name
      if (!matchedOutcome) {
        for (const [outcomeName, outcomeGroup] of Object.entries(grouped)) {
          const outcomeNameNormalized = normalizeTitle(outcomeName);

          // Check if sectionTitle matches outcome name
          if (
            normalizedSectionTitle.includes(outcomeNameNormalized) ||
            outcomeNameNormalized.includes(normalizedSectionTitle)
          ) {
            matchedOutcome = outcomeName;

            // Add to first available outline or create "Other"
            if (Object.keys(outcomeGroup.outlines).length > 0) {
              matchedOutline = Object.keys(outcomeGroup.outlines)[0];
            } else {
              if (!outcomeGroup.outlines["Other"]) {
                outcomeGroup.outlines["Other"] = {
                  section: { title: "Other", sectionId: "other" },
                  assessments: [],
                };
              }
              matchedOutline = "Other";
            }
            break;
          }
        }
      }

      // If no match found, try fuzzy matching
      if (!matchedOutcome) {
        for (const [outcomeName, outcomeGroup] of Object.entries(grouped)) {
          const outcomeNameNormalized = normalizeTitle(outcomeName);
          const tokens = normalizedSectionTitle.split(/\s+/).filter(Boolean);
          const outcomeTokens = outcomeNameNormalized
            .split(/\s+/)
            .filter(Boolean);
          const overlap = tokens.filter((t) => outcomeTokens.includes(t));

          if (overlap.length >= 2) {
            matchedOutcome = outcomeName;
            if (Object.keys(outcomeGroup.outlines).length > 0) {
              matchedOutline = Object.keys(outcomeGroup.outlines)[0];
            } else {
              if (!outcomeGroup.outlines["Other"]) {
                outcomeGroup.outlines["Other"] = {
                  section: { title: "Other", sectionId: "other" },
                  assessments: [],
                };
              }
              matchedOutline = "Other";
            }
            break;
          }
        }
      }

      // If still no match, add to "Uncategorized"
      if (!matchedOutcome) {
        if (!grouped["Uncategorized"]) {
          grouped["Uncategorized"] = {
            outcome: { outcomeData: { name: "Uncategorized" } },
            outlines: {
              Other: {
                section: { title: "Other", sectionId: "other" },
                assessments: [],
              },
            },
          };
        }
        matchedOutcome = "Uncategorized";
        matchedOutline = "Other";
      }

      if (matchedOutcome && matchedOutline) {
        grouped[matchedOutcome].outlines[matchedOutline].assessments.push(
          assessmentData
        );
      } else {
        // This shouldn't happen, but log if it does
        console.warn("[groupedAssessmentsByOutcome] Assessment not matched:", {
          sectionTitle,
          unitAssessmentsLength: unitAssessments.length,
          outcomesLength: outcomes.length,
          groupedKeys: Object.keys(grouped),
        });
      }
    });

    // Debug: Log the final grouped structure
    const totalAssessmentsInGrouped = Object.values(grouped).reduce(
      (sum, og) =>
        sum +
        Object.values(og.outlines).reduce(
          (s, ol) => s + ol.assessments.length,
          0
        ),
      0
    );

    console.log("[groupedAssessmentsByOutcome] Final grouped structure:", {
      unitAssessmentsCount: unitAssessments.length,
      outcomesCount: outcomes.length,
      groupedKeys: Object.keys(grouped),
      totalAssessmentsInGrouped,
      groupedDetails: Object.entries(grouped).map(([outcomeName, og]) => ({
        outcomeName,
        outlineCount: Object.keys(og.outlines).length,
        assessmentCount: Object.values(og.outlines).reduce(
          (s, ol) => s + ol.assessments.length,
          0
        ),
      })),
    });

    // If we have assessments but they're not showing up, ensure "Uncategorized" exists
    if (unitAssessments.length > 0 && totalAssessmentsInGrouped === 0) {
      console.warn(
        "[groupedAssessmentsByOutcome] ⚠️ Assessments exist but none matched! Creating Uncategorized group."
      );
      if (!grouped["Uncategorized"]) {
        grouped["Uncategorized"] = {
          outcome: { outcomeData: { name: "Uncategorized" } },
          outlines: {
            Other: {
              section: { title: "Other", sectionId: "other" },
              assessments: [],
            },
          },
        };
      }
      // Add all assessments to Uncategorized
      unitAssessments.forEach((detail, localIdx) => {
        const historyIndex =
          typeof detail.historyIndex === "number" ? detail.historyIndex : null;
        const baseEntry =
          historyIndex !== null && modalAssessments[historyIndex]
            ? modalAssessments[historyIndex]
            : null;
        const fallbackEntry = {
          sectionTitle:
            detail.sectionTitle ||
            detail.outcomeName ||
            `Assessment ${localIdx + 1}`,
          assessment: {
            percent: typeof detail.percent === "number" ? detail.percent : null,
            score: typeof detail.score === "number" ? detail.score : null,
            maxScore:
              typeof detail.maxScore === "number" ? detail.maxScore : null,
          },
        };
        const entry = baseEntry || fallbackEntry;
        grouped["Uncategorized"].outlines["Other"].assessments.push({
          entry,
          idx: historyIndex !== null ? historyIndex : localIdx + 1000,
        });
      });
    }

    return grouped;
  }, [
    selectedUnit,
    modalAssessments,
    unitOutcomesData,
    currentBlockKey,
    learnerProgressByBlock,
    selectedUnitName,
  ]);

  const visibleAssessments = useMemo(() => {
    // Flatten grouped assessments for backward compatibility
    const flat = [];
    Object.values(groupedAssessmentsByOutcome).forEach((outcomeGroup) => {
      Object.values(outcomeGroup.outlines).forEach((outlineGroup) => {
        outlineGroup.assessments.forEach((assessment) => {
          flat.push(assessment);
        });
      });
    });
    return flat;
  }, [groupedAssessmentsByOutcome]);

  return (
    <div className="hero-dashboard-content">
      {/* Professional Header with Total Average */}
      <div className="unity-progress-header">
        <div className="unity-header-content">
          <div className="unity-header-left">
            <h1>
              <span className="header-icon">📈</span>
              Unity Learning Progress
            </h1>
            <p>Track learner performance across all programs and assessments</p>
          </div>
          <div className="total-average-card">
            <div className="total-average-label">Overall Average</div>
            <div className="total-average-value">{totalAverage}%</div>
          </div>
        </div>
      </div>

      {error && <div className="error-alert">⚠️ {String(error)}</div>}

      <div className="table-container">
        {loading && (
          <div className="loading-state">Loading intake blocks...</div>
        )}

        {intakeBlocks.length === 0 && !loading && (
          <div className="empty-state">
            No intake blocks with learners found
          </div>
        )}

        {intakeBlocks.map((block) => {
          const programLabel =
            block?.programName ||
            block?.program ||
            block?.name ||
            block?.id ||
            "Unknown";
          const blockKey = block?.id || programLabel;
          const grouped = learnerProgressByBlock[blockKey];
          const allLearners = grouped?.learners || [];
          const modules =
            grouped?.programStructure?.modules &&
            Array.isArray(grouped.programStructure.modules)
              ? [...grouped.programStructure.modules].sort(
                  (a, b) => (a?.sequence || 0) - (b?.sequence || 0)
                )
              : [];
          const paginatedLearners = getPaginatedLearners(allLearners, blockKey);
          const isCollapsed = collapsedBlocks.has(blockKey);
          const blockAverage = calculateBlockAverage(blockKey);
          const currentPage = getCurrentPage(blockKey);
          const totalPages = getTotalPages(allLearners);

          return (
            <div key={blockKey} className="program-card">
              <div
                className="program-card-header"
                onClick={() => toggleBlockCollapse(blockKey)}
              >
                <div className="program-header-content">
                  <div className="program-header-left">
                    <h2 className="program-title">{programLabel}</h2>
                    <p className="program-note">
                      {allLearners.length} learner
                      {allLearners.length !== 1 ? "s" : ""} • Average:{" "}
                      {blockAverage}%
                    </p>
                  </div>
                  <div className="collapse-icon">
                    <span
                      style={{
                        transform: isCollapsed
                          ? "rotate(0deg)"
                          : "rotate(180deg)",
                        transition: "transform 0.3s ease",
                        display: "inline-block",
                      }}
                    >
                      ▼
                    </span>
                  </div>
                </div>
              </div>

              {!isCollapsed && (
                <>
                  <div className="table-responsive">
                    {allLearners.length === 0 && (
                      <div className="empty-state">
                        {progressLoading
                          ? "Calculating progress..."
                          : "No learners found for this intake block"}
                      </div>
                    )}

                    {paginatedLearners.map((row, index) => {
                      const rowNumber =
                        (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
                      const learnerName =
                        row.learnerName || row.learnerEmail.split("@")[0];
                      const initials = learnerName
                        .split(" ")
                        .map((n) => n[0])
                        .join("")
                        .toUpperCase()
                        .substring(0, 2);

                      return (
                        <div
                          key={`${blockKey}-${row.learnerEmail}-${index}`}
                          className="learner-row"
                        >
                          <div className="row-number">{rowNumber}</div>
                          <div
                            className="learner-info"
                            onClick={() =>
                              handleOpenAssessmentsModal(
                                blockKey,
                                row.learnerEmail
                              )
                            }
                          >
                            <div className="profile-circle">{initials}</div>
                            <div className="learner-details">
                              <div className="learner-name">{learnerName}</div>
                              <div className="learner-email">
                                {row.learnerEmail}
                              </div>
                            </div>
                          </div>
                          {(() => {
                            const componentList =
                              row.componentProgress?.components || [];

                            // Group components by module name
                            const modulesMap = new Map();
                            componentList.forEach((component) => {
                              const moduleName =
                                component.moduleName || "Module";
                              if (!modulesMap.has(moduleName)) {
                                modulesMap.set(moduleName, {
                                  moduleName,
                                  totalAssessments:
                                    component.assessmentsCount || 0,
                                  firstComponentKey: component.componentKey,
                                });
                              } else {
                                const group = modulesMap.get(moduleName);
                                group.totalAssessments +=
                                  component.assessmentsCount || 0;
                              }
                            });

                            // For every module in the program structure, render a single module cell
                            return modules.map((module, moduleIndex) => {
                              const summary =
                                modulesMap.get(module.name) || null;

                              if (!summary) {
                                return (
                                  <div
                                    key={module.name || moduleIndex}
                                    className="components-cell"
                                  >
                                    <span className="components-empty">—</span>
                                  </div>
                                );
                              }

                              return (
                                <div
                                  key={module.name || moduleIndex}
                                  className="components-cell"
                                >
                                  <button
                                    type="button"
                                    className="component-chip"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenAssessmentsModal(
                                        blockKey,
                                        row.learnerEmail,
                                        summary.firstComponentKey
                                      );
                                    }}
                                  >
                                    <span className="component-chip-name">
                                      {`M${moduleIndex + 1}`}
                                    </span>
                                    <span className="component-chip-count">
                                      {summary.totalAssessments} done
                                    </span>
                                  </button>
                                </div>
                              );
                            });
                          })()}
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="pagination-container">
                      <button
                        className="pagination-btn"
                        onClick={() =>
                          setCurrentPage(blockKey, currentPage - 1)
                        }
                        disabled={currentPage === 1}
                      >
                        ‹ Previous
                      </button>

                      <div className="page-numbers">
                        {Array.from(
                          { length: Math.min(5, totalPages) },
                          (_, i) => {
                            const pageNum = Math.max(1, currentPage - 2) + i;
                            if (pageNum > totalPages) return null;
                            return (
                              <button
                                key={pageNum}
                                className={`page-number ${
                                  currentPage === pageNum ? "active" : ""
                                }`}
                                onClick={() =>
                                  setCurrentPage(blockKey, pageNum)
                                }
                              >
                                {pageNum}
                              </button>
                            );
                          }
                        )}
                      </div>

                      <button
                        className="pagination-btn"
                        onClick={() =>
                          setCurrentPage(blockKey, currentPage + 1)
                        }
                        disabled={currentPage === totalPages}
                      >
                        Next ›
                      </button>

                      <div className="pagination-info">
                        Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                        {Math.min(
                          currentPage * ITEMS_PER_PAGE,
                          allLearners.length
                        )}{" "}
                        of {allLearners.length} learners
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {showModal && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "#fff",
              width: "min(780px, 92vw)",
              maxHeight: "90vh",
              borderRadius: 14,
              overflow: "hidden",
              boxShadow: "0 14px 40px rgba(2,6,23,0.25)",
              border: "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                borderBottom: "2px solid #e2e8f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                flexShrink: 0,
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: 700,
                    color: "#1e40af",
                    fontSize: 17,
                    marginBottom: "4px",
                  }}
                >
                  📝 Written Assessments
                </div>
                <div
                  style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}
                >
                  {selectedLearner}
                </div>
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button
                  onClick={handleExpandAll}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 6,
                    border: "2px solid #3b82f6",
                    background: "#fff",
                    color: "#3b82f6",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: 12,
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#eff6ff";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#fff";
                  }}
                >
                  {expandAll ? "Collapse All ▲" : "Expand All ▼"}
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    border: 0,
                    background: "transparent",
                    fontSize: 22,
                    cursor: "pointer",
                    color: "#64748b",
                    fontWeight: "bold",
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#e2e8f0";
                    e.target.style.color = "#1e293b";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "transparent";
                    e.target.style.color = "#64748b";
                  }}
                >
                  ✕
                </button>
              </div>
            </div>
            <div
              style={{
                padding: 12,
                overflowY: "auto",
                overflowX: "hidden",
                flex: 1,
                minHeight: 0,
                maxHeight: "calc(90vh - 140px)",
              }}
            >
              <div
                style={{
                  padding: "12px 16px",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "8px",
                  marginBottom: "12px",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    color: "#1e40af",
                    marginBottom: "4px",
                  }}
                >
                  📝 Review Instructions
                </div>
                <div style={{ fontSize: "13px", color: "#475569" }}>
                  Review each learner's answers below, then adjust the grade if
                  needed. Click on any assessment to expand and see detailed
                  questions and answers.
                </div>
              </div>

              {modalComponentProgress && (
                <div className="component-progress-panel">
                  <div className="component-progress-header">
                    <div>
                      <div className="component-progress-title">
                        Component Progress
                      </div>
                      <p>
                        Choose a component to view its five units. Select a unit
                        to inspect every assessment the learner has completed.
                      </p>
                    </div>
                  </div>
                  <div className="component-selector">
                    <div className="component-list">
                      {modalComponentProgress.components.length === 0 && (
                        <div className="component-progress-feedback">
                          No component structure available for this program yet.
                        </div>
                      )}
                      {modalComponentProgress.components.map((component) => {
                        const isActive =
                          component.componentKey === selectedComponentKey;
                        const componentAvg =
                          calculateComponentAverage(component);
                        return (
                          <button
                            type="button"
                            key={component.componentKey}
                            className={`component-list-item ${
                              isActive ? "active" : ""
                            }`}
                            onClick={() =>
                              handleSelectComponent(component.componentKey)
                            }
                          >
                            <span className="component-list-name">
                              {component.componentName}
                            </span>
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                                gap: 2,
                              }}
                            >
                              <span className="component-list-count">
                                {component.assessmentsCount || 0} assessments
                              </span>
                              {componentAvg !== null && (
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: "#64748b",
                                    fontWeight: 600,
                                  }}
                                >
                                  Avg: {componentAvg}%
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <div className="unit-list">
                      {selectedComponent ? (
                        selectedComponent.units.map((unit) => {
                          const isUnitActive =
                            unit.unitName === selectedUnitName ||
                            (!selectedUnitName &&
                              unit.unitName ===
                                selectedComponent.units[0]?.unitName);
                          const unitAvg = calculateUnitAverage(unit);
                          return (
                            <button
                              type="button"
                              key={unit.unitId}
                              className={`unit-list-item ${
                                isUnitActive ? "active" : ""
                              }`}
                              onClick={() => handleSelectUnit(unit.unitName)}
                            >
                              <div className="unit-list-title">
                                {unit.unitName}
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-end",
                                  gap: 2,
                                }}
                              >
                                <div className="unit-list-count">
                                  {unit.assessments.length} assessment
                                  {unit.assessments.length === 1 ? "" : "s"}
                                </div>
                                {unitAvg !== null && (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      color: "#64748b",
                                      fontWeight: 600,
                                    }}
                                  >
                                    Avg: {unitAvg}%
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })
                      ) : (
                        <div className="unit-list-empty">
                          Select a component to view its units.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {loadingUnitData ? (
                <div className="component-progress-feedback">
                  Loading unit structure...
                </div>
              ) : Object.keys(groupedAssessmentsByOutcome).length > 0 ? (
                <>
                  {/* Hierarchical Display: Outcomes -> Outlines -> Assessments */}
                  {Object.entries(groupedAssessmentsByOutcome).map(
                    ([outcomeName, outcomeGroup]) => {
                      const isOutcomeExpanded =
                        expandedOutcomes.has(outcomeName);
                      const outcomeTitle =
                        outcomeGroup.outcome?.outcomeData?.name || outcomeName;

                      return (
                        <div key={outcomeName} style={{ marginBottom: "16px" }}>
                          {/* Outcome Header */}
                          <div
                            className="outcome-header"
                            style={{
                              background: "#eff6ff",
                              border: "1px solid #bfdbfe",
                              borderRadius: "8px",
                              padding: "12px 16px",
                              cursor: "pointer",
                              marginBottom: "8px",
                              transition: "all 0.2s ease",
                            }}
                            onClick={() => toggleOutcome(outcomeName)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#dbeafe";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#eff6ff";
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                              }}
                            >
                              <span
                                style={{
                                  transform: isOutcomeExpanded
                                    ? "rotate(90deg)"
                                    : "rotate(0)",
                                  transition: "transform 0.2s",
                                  color: "#2563eb",
                                  fontSize: "14px",
                                  fontWeight: "bold",
                                }}
                              >
                                ▶
                              </span>
                              <span
                                style={{
                                  fontWeight: 700,
                                  color: "#1e40af",
                                  fontSize: "15px",
                                }}
                              >
                                📋 {outcomeTitle}
                              </span>
                            </div>
                          </div>

                          {/* Outlines under this outcome */}
                          {isOutcomeExpanded && (
                            <div style={{ marginLeft: "24px" }}>
                              {Object.entries(outcomeGroup.outlines).map(
                                ([outlineTitle, outlineGroup]) => {
                                  const isOutlineExpanded =
                                    expandedOutlines.has(
                                      `${outcomeName}|||${outlineTitle}`
                                    );
                                  const assessments =
                                    outlineGroup?.assessments || [];

                                  // Show outline even if no assessments

                                  return (
                                    <div
                                      key={outlineTitle}
                                      style={{ marginBottom: "12px" }}
                                    >
                                      {/* Outline Header */}
                                      <div
                                        className="outline-header"
                                        style={{
                                          background: "#f8fafc",
                                          border: "1px solid #e2e8f0",
                                          borderRadius: "6px",
                                          padding: "10px 14px",
                                          cursor: "pointer",
                                          marginBottom: "6px",
                                          transition: "all 0.2s ease",
                                        }}
                                        onClick={() =>
                                          toggleOutline(
                                            outcomeName,
                                            outlineTitle
                                          )
                                        }
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.background =
                                            "#f1f5f9";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.background =
                                            "#f8fafc";
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "10px",
                                          }}
                                        >
                                          <span
                                            style={{
                                              transform: isOutlineExpanded
                                                ? "rotate(90deg)"
                                                : "rotate(0)",
                                              transition: "transform 0.2s",
                                              color: "#64748b",
                                              fontSize: "12px",
                                              fontWeight: "bold",
                                            }}
                                          >
                                            ▶
                                          </span>
                                          <span
                                            style={{
                                              fontWeight: 600,
                                              color: "#475569",
                                              fontSize: "14px",
                                            }}
                                          >
                                            📄{" "}
                                            {outlineGroup.section?.title ||
                                              outlineTitle}
                                          </span>
                                          <span
                                            style={{
                                              fontSize: "12px",
                                              color: "#94a3b8",
                                              marginLeft: "auto",
                                            }}
                                          >
                                            {assessments.length} assessment
                                            {assessments.length === 1
                                              ? ""
                                              : "s"}
                                          </span>
                                        </div>
                                      </div>

                                      {/* Assessments under this outline */}
                                      {isOutlineExpanded && (
                                        <div style={{ marginLeft: "20px" }}>
                                          {assessments.length > 0 ? (
                                            <>
                                              <div
                                                style={{
                                                  display: "grid",
                                                  gridTemplateColumns:
                                                    "1fr 120px 120px",
                                                  fontWeight: 600,
                                                  borderBottom:
                                                    "2px solid #e2e8f0",
                                                  color: "#1e40af",
                                                  background: "#f8fafc",
                                                  padding: "8px",
                                                  fontSize: "12px",
                                                }}
                                              >
                                                <div
                                                  style={{ padding: "0 8px" }}
                                                >
                                                  Assessment
                                                </div>
                                                <div
                                                  style={{
                                                    padding: "0 8px",
                                                    textAlign: "right",
                                                  }}
                                                >
                                                  Current %
                                                </div>
                                                <div
                                                  style={{
                                                    padding: "0 8px",
                                                    textAlign: "right",
                                                  }}
                                                >
                                                  Adjust Grade
                                                </div>
                                              </div>
                                              {assessments.map(
                                                ({ entry: a, idx }) => {
                                                  const isOpen =
                                                    expandedAssessments.has(
                                                      idx
                                                    ) || expandAll;

                                                  // Get assessment details if available
                                                  const unitId =
                                                    buildUnitDocumentId(
                                                      selectedUnitName,
                                                      currentProgramName
                                                    );
                                                  let outcomeId = outcomeName;
                                                  let outlineId = outlineTitle;

                                                  // Try to get outlineId from section
                                                  if (
                                                    unitOutcomesData?.outcomes &&
                                                    outcomeName
                                                  ) {
                                                    const outcome =
                                                      unitOutcomesData.outcomes.find(
                                                        (o) =>
                                                          (o.outcomeData
                                                            ?.name ||
                                                            o.name) ===
                                                          outcomeName
                                                      );
                                                    if (
                                                      outcome &&
                                                      outlineTitle &&
                                                      outcome.outline?.sections
                                                    ) {
                                                      const section =
                                                        outcome.outline.sections.find(
                                                          (s) =>
                                                            (s.title ||
                                                              s.sectionId) ===
                                                            outlineTitle
                                                        );
                                                      if (section) {
                                                        outlineId =
                                                          section.sectionId ||
                                                          section.id ||
                                                          outlineTitle;
                                                      }
                                                    }
                                                  }

                                                  const cacheKey =
                                                    selectedLearner &&
                                                    unitId &&
                                                    outcomeId &&
                                                    outlineId
                                                      ? `${selectedLearner}|||${unitId}|||${outcomeId}|||${outlineId}`
                                                      : null;
                                                  const assessmentDetails =
                                                    cacheKey
                                                      ? fetchedAssessmentDetails[
                                                          cacheKey
                                                        ]
                                                      : null;
                                                  const isLoadingDetails =
                                                    cacheKey
                                                      ? loadingAssessmentDetails[
                                                          cacheKey
                                                        ]
                                                      : false;

                                                  // Use questions from entry if available, otherwise from fetched details
                                                  // Questions are stored at the ROOT level of the entry (e.questions)
                                                  let questionsToDisplay = null;

                                                  // Try multiple sources, prioritizing entry.questions (where they actually are)
                                                  if (
                                                    a.entry?.questions &&
                                                    Object.keys(
                                                      a.entry.questions
                                                    ).length > 0
                                                  ) {
                                                    questionsToDisplay =
                                                      a.entry.questions;
                                                  } else if (
                                                    a.questions &&
                                                    Object.keys(a.questions)
                                                      .length > 0
                                                  ) {
                                                    questionsToDisplay =
                                                      a.questions;
                                                  } else if (
                                                    a.assessment?.questions &&
                                                    Object.keys(
                                                      a.assessment.questions
                                                    ).length > 0
                                                  ) {
                                                    questionsToDisplay =
                                                      a.assessment.questions;
                                                  } else if (
                                                    assessmentDetails?.questions &&
                                                    Object.keys(
                                                      assessmentDetails.questions
                                                    ).length > 0
                                                  ) {
                                                    questionsToDisplay =
                                                      assessmentDetails.questions;
                                                  }

                                                  // Debug logging
                                                  if (isOpen && idx === 0) {
                                                    console.log(
                                                      "[Display] Questions lookup:",
                                                      {
                                                        "a.entry?.questions": a
                                                          .entry?.questions
                                                          ? Object.keys(
                                                              a.entry.questions
                                                            ).length
                                                          : 0,
                                                        "a.questions":
                                                          a.questions
                                                            ? Object.keys(
                                                                a.questions
                                                              ).length
                                                            : 0,
                                                        "a.assessment?.questions":
                                                          a.assessment
                                                            ?.questions
                                                            ? Object.keys(
                                                                a.assessment
                                                                  .questions
                                                              ).length
                                                            : 0,
                                                        "assessmentDetails?.questions":
                                                          assessmentDetails?.questions
                                                            ? Object.keys(
                                                                assessmentDetails.questions
                                                              ).length
                                                            : 0,
                                                        questionsToDisplay:
                                                          questionsToDisplay
                                                            ? Object.keys(
                                                                questionsToDisplay
                                                              ).length
                                                            : 0,
                                                        "a.entry keys": a.entry
                                                          ? Object.keys(a.entry)
                                                          : null,
                                                        "a.entry.questions sample":
                                                          a.entry?.questions
                                                            ? Object.keys(
                                                                a.entry
                                                                  .questions
                                                              ).slice(0, 3)
                                                            : null,
                                                      }
                                                    );
                                                  }

                                                  if (isOpen && idx === 0) {
                                                    console.log(
                                                      "[Display] Assessment entry for display:",
                                                      {
                                                        idx,
                                                        sectionTitle:
                                                          a.sectionTitle,
                                                        hasQuestions:
                                                          !!a.questions,
                                                        hasEntryQuestions:
                                                          !!a.entry?.questions,
                                                        hasAssessmentQuestions:
                                                          !!a.assessment
                                                            ?.questions,
                                                        hasFetchedDetails:
                                                          !!assessmentDetails?.questions,
                                                        questionsToDisplay:
                                                          questionsToDisplay
                                                            ? Object.keys(
                                                                questionsToDisplay
                                                              ).length
                                                            : 0,
                                                        aKeys: Object.keys(
                                                          a || {}
                                                        ),
                                                        entryKeys: a.entry
                                                          ? Object.keys(a.entry)
                                                          : null,
                                                      }
                                                    );
                                                  }

                                                  return (
                                                    <div key={idx}>
                                                      <div
                                                        style={{
                                                          display: "grid",
                                                          gridTemplateColumns:
                                                            "1fr 120px 120px",
                                                          borderBottom:
                                                            "1px solid #e2e8f0",
                                                          alignItems: "center",
                                                          cursor: "pointer",
                                                          background: isOpen
                                                            ? "#f0f9ff"
                                                            : "#ffffff",
                                                          transition:
                                                            "all 0.2s ease",
                                                        }}
                                                        onClick={() => {
                                                          const assessmentSectionTitle =
                                                            a.sectionTitle ||
                                                            a.entry
                                                              ?.sectionTitle;
                                                          toggleExpandAssessment(
                                                            idx,
                                                            outcomeName,
                                                            assessmentSectionTitle ||
                                                              outlineTitle
                                                          );
                                                        }}
                                                        onMouseEnter={(e) => {
                                                          if (!isOpen)
                                                            e.currentTarget.style.background =
                                                              "#f8fafc";
                                                        }}
                                                        onMouseLeave={(e) => {
                                                          if (!isOpen)
                                                            e.currentTarget.style.background =
                                                              "#ffffff";
                                                        }}
                                                      >
                                                        <div
                                                          style={{
                                                            padding: "12px 8px",
                                                            display: "flex",
                                                            alignItems:
                                                              "center",
                                                            gap: 10,
                                                          }}
                                                        >
                                                          <span
                                                            style={{
                                                              transform: isOpen
                                                                ? "rotate(90deg)"
                                                                : "rotate(0)",
                                                              transition:
                                                                "transform 0.2s",
                                                              color: isOpen
                                                                ? "#2563eb"
                                                                : "#64748b",
                                                              fontSize: "12px",
                                                              fontWeight:
                                                                "bold",
                                                            }}
                                                          >
                                                            ▶
                                                          </span>
                                                          <div
                                                            style={{
                                                              display: "flex",
                                                              flexDirection:
                                                                "column",
                                                              gap: "2px",
                                                            }}
                                                          >
                                                            <span
                                                              style={{
                                                                fontWeight: 600,
                                                                color:
                                                                  "#1e293b",
                                                                fontSize:
                                                                  "13px",
                                                              }}
                                                            >
                                                              {a.sectionTitle ||
                                                                "—"}
                                                            </span>
                                                            {!isOpen && (
                                                              <span
                                                                style={{
                                                                  fontSize:
                                                                    "10px",
                                                                  color:
                                                                    "#64748b",
                                                                }}
                                                              >
                                                                {
                                                                  Object.keys(
                                                                    a.questions ||
                                                                      a.entry
                                                                        ?.questions ||
                                                                      a
                                                                        .assessment
                                                                        ?.questions ||
                                                                      {}
                                                                  ).length
                                                                }{" "}
                                                                questions •
                                                                Click to view
                                                              </span>
                                                            )}
                                                          </div>
                                                        </div>
                                                        <div
                                                          style={{
                                                            padding: "12px 8px",
                                                            textAlign: "right",
                                                            color: "#0f172a",
                                                            fontWeight: 600,
                                                          }}
                                                        >
                                                          <span
                                                            style={{
                                                              background:
                                                                "#dbeafe",
                                                              padding:
                                                                "4px 10px",
                                                              borderRadius:
                                                                "6px",
                                                              color: "#1e40af",
                                                              border:
                                                                "1px solid #bfdbfe",
                                                              fontSize: "12px",
                                                            }}
                                                          >
                                                            {typeof a.assessment
                                                              ?.percent ===
                                                            "number"
                                                              ? `${a.assessment.percent}%`
                                                              : typeof a.percent ===
                                                                "number"
                                                              ? `${a.percent}%`
                                                              : "—"}
                                                          </span>
                                                        </div>
                                                        <div
                                                          style={{
                                                            padding: "8px",
                                                            textAlign: "right",
                                                            display: "flex",
                                                            justifyContent:
                                                              "flex-end",
                                                            alignItems:
                                                              "center",
                                                          }}
                                                          onClick={(e) =>
                                                            e.stopPropagation()
                                                          }
                                                        >
                                                          <div
                                                            style={{
                                                              position:
                                                                "relative",
                                                            }}
                                                          >
                                                            <input
                                                              type="number"
                                                              min={0}
                                                              max={100}
                                                              step={1}
                                                              defaultValue={
                                                                a.assessment
                                                                  ?.percent ??
                                                                a.percent ??
                                                                0
                                                              }
                                                              onChange={(e) =>
                                                                handleEditAssessmentPercent(
                                                                  idx,
                                                                  Number(
                                                                    e.target
                                                                      .value
                                                                  )
                                                                )
                                                              }
                                                              placeholder="0-100"
                                                              style={{
                                                                width: 80,
                                                                padding:
                                                                  "6px 24px 6px 10px",
                                                                border:
                                                                  "2px solid #3b82f6",
                                                                borderRadius: 6,
                                                                background:
                                                                  "#fff",
                                                                fontSize:
                                                                  "12px",
                                                                fontWeight: 600,
                                                                color:
                                                                  "#1e40af",
                                                              }}
                                                            />
                                                            <span
                                                              style={{
                                                                position:
                                                                  "absolute",
                                                                right: "8px",
                                                                top: "50%",
                                                                transform:
                                                                  "translateY(-50%)",
                                                                color:
                                                                  "#64748b",
                                                                fontSize:
                                                                  "11px",
                                                                fontWeight: 600,
                                                                pointerEvents:
                                                                  "none",
                                                              }}
                                                            >
                                                              %
                                                            </span>
                                                          </div>
                                                        </div>
                                                      </div>
                                                      {isOpen && (
                                                        <>
                                                          <div
                                                            style={{
                                                              padding: "16px",
                                                              background:
                                                                "#ffffff",
                                                              borderBottom:
                                                                "2px solid #e2e8f0",
                                                            }}
                                                          >
                                                            {/* Score Breakdown */}
                                                            <div
                                                              style={{
                                                                background:
                                                                  "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                                                                padding: "16px",
                                                                borderRadius:
                                                                  "10px",
                                                                border:
                                                                  "2px solid #3b82f6",
                                                                marginBottom:
                                                                  "16px",
                                                                boxShadow:
                                                                  "0 4px 12px rgba(59, 130, 246, 0.15)",
                                                              }}
                                                            >
                                                              <div
                                                                style={{
                                                                  fontSize:
                                                                    "13px",
                                                                  fontWeight: 700,
                                                                  color:
                                                                    "#1e40af",
                                                                  marginBottom:
                                                                    "12px",
                                                                  display:
                                                                    "flex",
                                                                  alignItems:
                                                                    "center",
                                                                  gap: "8px",
                                                                }}
                                                              >
                                                                <span
                                                                  style={{
                                                                    fontSize:
                                                                      "18px",
                                                                  }}
                                                                >
                                                                  📊
                                                                </span>
                                                                <span>
                                                                  Assessment
                                                                  Score
                                                                  Breakdown
                                                                </span>
                                                              </div>
                                                              <div
                                                                style={{
                                                                  display:
                                                                    "grid",
                                                                  gridTemplateColumns:
                                                                    "repeat(3, 1fr)",
                                                                  gap: "16px",
                                                                  textAlign:
                                                                    "center",
                                                                }}
                                                              >
                                                                <div
                                                                  style={{
                                                                    background:
                                                                      "#ffffff",
                                                                    padding:
                                                                      "12px",
                                                                    borderRadius:
                                                                      "8px",
                                                                    border:
                                                                      "1px solid #bfdbfe",
                                                                  }}
                                                                >
                                                                  <div
                                                                    style={{
                                                                      fontSize:
                                                                        "10px",
                                                                      color:
                                                                        "#64748b",
                                                                      fontWeight: 600,
                                                                      textTransform:
                                                                        "uppercase",
                                                                      marginBottom:
                                                                        "6px",
                                                                    }}
                                                                  >
                                                                    Score
                                                                    Achieved
                                                                  </div>
                                                                  <div
                                                                    style={{
                                                                      fontSize:
                                                                        "28px",
                                                                      fontWeight: 800,
                                                                      color:
                                                                        "#3b82f6",
                                                                    }}
                                                                  >
                                                                    {a
                                                                      .assessment
                                                                      ?.score ??
                                                                      "—"}
                                                                  </div>
                                                                </div>
                                                                <div
                                                                  style={{
                                                                    background:
                                                                      "#ffffff",
                                                                    padding:
                                                                      "12px",
                                                                    borderRadius:
                                                                      "8px",
                                                                    border:
                                                                      "1px solid #bfdbfe",
                                                                  }}
                                                                >
                                                                  <div
                                                                    style={{
                                                                      fontSize:
                                                                        "10px",
                                                                      color:
                                                                        "#64748b",
                                                                      fontWeight: 600,
                                                                      textTransform:
                                                                        "uppercase",
                                                                      marginBottom:
                                                                        "6px",
                                                                    }}
                                                                  >
                                                                    Max Score
                                                                  </div>
                                                                  <div
                                                                    style={{
                                                                      fontSize:
                                                                        "28px",
                                                                      fontWeight: 800,
                                                                      color:
                                                                        "#1e293b",
                                                                    }}
                                                                  >
                                                                    {a
                                                                      .assessment
                                                                      ?.maxScore ??
                                                                      "—"}
                                                                  </div>
                                                                </div>
                                                                <div
                                                                  style={{
                                                                    background:
                                                                      "#ffffff",
                                                                    padding:
                                                                      "12px",
                                                                    borderRadius:
                                                                      "8px",
                                                                    border:
                                                                      "1px solid #bfdbfe",
                                                                  }}
                                                                >
                                                                  <div
                                                                    style={{
                                                                      fontSize:
                                                                        "10px",
                                                                      color:
                                                                        "#64748b",
                                                                      fontWeight: 600,
                                                                      textTransform:
                                                                        "uppercase",
                                                                      marginBottom:
                                                                        "6px",
                                                                    }}
                                                                  >
                                                                    Final Grade
                                                                  </div>
                                                                  <div
                                                                    style={{
                                                                      fontSize:
                                                                        "28px",
                                                                      fontWeight: 800,
                                                                      color:
                                                                        "#10b981",
                                                                    }}
                                                                  >
                                                                    {typeof a
                                                                      .assessment
                                                                      ?.percent ===
                                                                    "number"
                                                                      ? `${a.assessment.percent}%`
                                                                      : "—"}
                                                                  </div>
                                                                </div>
                                                              </div>
                                                            </div>
                                                          </div>

                                                          {/* Questions and Answers */}
                                                          {isLoadingDetails && (
                                                            <div
                                                              style={{
                                                                padding: "16px",
                                                                textAlign:
                                                                  "center",
                                                                color:
                                                                  "#64748b",
                                                                fontSize:
                                                                  "13px",
                                                              }}
                                                            >
                                                              Loading assessment
                                                              details...
                                                            </div>
                                                          )}
                                                          {!isLoadingDetails &&
                                                            assessmentDetails && (
                                                              <div
                                                                style={{
                                                                  marginTop:
                                                                    "16px",
                                                                  background:
                                                                    "#f8fafc",
                                                                  padding:
                                                                    "16px",
                                                                  borderRadius:
                                                                    "8px",
                                                                  border:
                                                                    "1px solid #e2e8f0",
                                                                }}
                                                              >
                                                                <div
                                                                  style={{
                                                                    fontSize:
                                                                      "14px",
                                                                    fontWeight: 700,
                                                                    color:
                                                                      "#1e40af",
                                                                    marginBottom:
                                                                      "12px",
                                                                  }}
                                                                >
                                                                  📝 Questions &
                                                                  Answers
                                                                </div>
                                                                {questionsToDisplay &&
                                                                Object.keys(
                                                                  questionsToDisplay
                                                                ).length > 0 ? (
                                                                  <div
                                                                    style={{
                                                                      display:
                                                                        "flex",
                                                                      flexDirection:
                                                                        "column",
                                                                      gap: "12px",
                                                                    }}
                                                                  >
                                                                    {Object.entries(
                                                                      questionsToDisplay
                                                                    ).map(
                                                                      (
                                                                        [
                                                                          questionId,
                                                                          questionData,
                                                                        ],
                                                                        qIdx
                                                                      ) => (
                                                                        <div
                                                                          key={
                                                                            questionId
                                                                          }
                                                                          style={{
                                                                            background:
                                                                              "#ffffff",
                                                                            padding:
                                                                              "12px",
                                                                            borderRadius:
                                                                              "6px",
                                                                            border:
                                                                              "1px solid #e2e8f0",
                                                                          }}
                                                                        >
                                                                          <div
                                                                            style={{
                                                                              fontSize:
                                                                                "12px",
                                                                              fontWeight: 600,
                                                                              color:
                                                                                "#475569",
                                                                              marginBottom:
                                                                                "6px",
                                                                            }}
                                                                          >
                                                                            Question{" "}
                                                                            {qIdx +
                                                                              1}
                                                                            :
                                                                          </div>
                                                                          <div
                                                                            style={{
                                                                              fontSize:
                                                                                "13px",
                                                                              color:
                                                                                "#1e293b",
                                                                              marginBottom:
                                                                                "8px",
                                                                            }}
                                                                          >
                                                                            {questionData.question ||
                                                                              questionData.text ||
                                                                              questionId}
                                                                          </div>
                                                                          {questionData.answer !==
                                                                            undefined && (
                                                                            <div>
                                                                              <div
                                                                                style={{
                                                                                  fontSize:
                                                                                    "11px",
                                                                                  fontWeight: 600,
                                                                                  color:
                                                                                    "#64748b",
                                                                                  marginBottom:
                                                                                    "4px",
                                                                                }}
                                                                              >
                                                                                Answer:
                                                                              </div>
                                                                              <div
                                                                                style={{
                                                                                  fontSize:
                                                                                    "12px",
                                                                                  color:
                                                                                    "#059669",
                                                                                  background:
                                                                                    "#d1fae5",
                                                                                  padding:
                                                                                    "6px 10px",
                                                                                  borderRadius:
                                                                                    "4px",
                                                                                }}
                                                                              >
                                                                                {typeof questionData.answer ===
                                                                                "object"
                                                                                  ? JSON.stringify(
                                                                                      questionData.answer,
                                                                                      null,
                                                                                      2
                                                                                    )
                                                                                  : String(
                                                                                      questionData.answer
                                                                                    )}
                                                                              </div>
                                                                            </div>
                                                                          )}
                                                                          {questionData.score !==
                                                                            undefined && (
                                                                            <div
                                                                              style={{
                                                                                fontSize:
                                                                                  "11px",
                                                                                color:
                                                                                  "#64748b",
                                                                                marginTop:
                                                                                  "6px",
                                                                              }}
                                                                            >
                                                                              Score:{" "}
                                                                              {
                                                                                questionData.score
                                                                              }{" "}
                                                                              /{" "}
                                                                              {questionData.maxScore ||
                                                                                "?"}
                                                                            </div>
                                                                          )}
                                                                        </div>
                                                                      )
                                                                    )}
                                                                  </div>
                                                                ) : !isLoadingDetails ? (
                                                                  <div
                                                                    style={{
                                                                      fontSize:
                                                                        "12px",
                                                                      color:
                                                                        "#94a3b8",
                                                                      fontStyle:
                                                                        "italic",
                                                                    }}
                                                                  >
                                                                    {assessmentDetails
                                                                      ? "No questions found in assessment details."
                                                                      : "Questions are not stored for this assessment."}
                                                                  </div>
                                                                ) : null}
                                                              </div>
                                                            )}
                                                          {!isLoadingDetails &&
                                                            !assessmentDetails &&
                                                            cacheKey && (
                                                              <div
                                                                style={{
                                                                  marginTop:
                                                                    "16px",
                                                                  background:
                                                                    "#fef3c7",
                                                                  padding:
                                                                    "12px",
                                                                  borderRadius:
                                                                    "6px",
                                                                  border:
                                                                    "1px solid #fde68a",
                                                                  fontSize:
                                                                    "12px",
                                                                  color:
                                                                    "#92400e",
                                                                }}
                                                              >
                                                                Assessment
                                                                details not
                                                                available.
                                                                Questions and
                                                                answers may not
                                                                be stored for
                                                                this assessment.
                                                              </div>
                                                            )}
                                                        </>
                                                      )}
                                                    </div>
                                                  );
                                                }
                                              )}
                                            </>
                                          ) : (
                                            <div
                                              style={{
                                                padding: "16px",
                                                textAlign: "center",
                                                color: "#94a3b8",
                                                fontSize: "13px",
                                                fontStyle: "italic",
                                              }}
                                            >
                                              No assessments recorded for this
                                              outline yet.
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }
                  )}
                </>
              ) : unitOutcomesData &&
                unitOutcomesData.outcomes &&
                unitOutcomesData.outcomes.length > 0 ? (
                <div className="component-progress-feedback">
                  Unit structure loaded but no assessments found. Select a unit
                  to view assessments.
                </div>
              ) : visibleAssessments.length === 0 ? (
                <div className="component-progress-feedback">
                  {loadingUnitData
                    ? "Loading unit structure..."
                    : "No assessments recorded for this unit yet."}
                </div>
              ) : (
                visibleAssessments.map(({ entry: a, idx }) => {
                  const isOpen = expandedAssessments.has(idx) || expandAll;

                  // Try to find outcomeName and outlineTitle from grouped structure
                  let outcomeName = null;
                  let outlineTitle = null;
                  for (const [outcomeKey, outcomeGroup] of Object.entries(
                    groupedAssessmentsByOutcome
                  )) {
                    for (const [outlineKey, outlineGroup] of Object.entries(
                      outcomeGroup.outlines
                    )) {
                      if (
                        outlineGroup.assessments.some((ass) => ass.idx === idx)
                      ) {
                        outcomeName = outcomeKey;
                        outlineTitle = outlineKey;
                        break;
                      }
                    }
                    if (outcomeName) break;
                  }

                  // Get assessment details if available
                  const unitId = buildUnitDocumentId(
                    selectedUnitName,
                    currentProgramName
                  );
                  let outcomeId = outcomeName;
                  let outlineId = outlineTitle;

                  // Try to get outlineId from section
                  if (unitOutcomesData?.outcomes && outcomeName) {
                    const outcome = unitOutcomesData.outcomes.find(
                      (o) => (o.outcomeData?.name || o.name) === outcomeName
                    );
                    if (outcome && outlineTitle && outcome.outline?.sections) {
                      const section = outcome.outline.sections.find(
                        (s) => (s.title || s.sectionId) === outlineTitle
                      );
                      if (section) {
                        outlineId =
                          section.sectionId || section.id || outlineTitle;
                      }
                    }
                  }

                  const cacheKey =
                    selectedLearner && unitId && outcomeId && outlineId
                      ? `${selectedLearner}|||${unitId}|||${outcomeId}|||${outlineId}`
                      : null;
                  const assessmentDetails = cacheKey
                    ? fetchedAssessmentDetails[cacheKey]
                    : null;
                  const isLoadingDetails = cacheKey
                    ? loadingAssessmentDetails[cacheKey]
                    : false;

                  // Use questions from entry if available, otherwise from fetched details
                  // Questions are stored at the ROOT level of the entry (e.questions)
                  let questionsToDisplay = null;

                  // Try multiple sources, prioritizing entry.questions (where they actually are)
                  if (
                    a.entry?.questions &&
                    Object.keys(a.entry.questions).length > 0
                  ) {
                    questionsToDisplay = a.entry.questions;
                  } else if (
                    a.questions &&
                    Object.keys(a.questions).length > 0
                  ) {
                    questionsToDisplay = a.questions;
                  } else if (
                    a.assessment?.questions &&
                    Object.keys(a.assessment.questions).length > 0
                  ) {
                    questionsToDisplay = a.assessment.questions;
                  } else if (
                    assessmentDetails?.questions &&
                    Object.keys(assessmentDetails.questions).length > 0
                  ) {
                    questionsToDisplay = assessmentDetails.questions;
                  }

                  // Debug logging for flat view
                  if (isOpen && idx === 0) {
                    console.log("[Display Flat] Questions lookup:", {
                      "a.entry?.questions": a.entry?.questions
                        ? Object.keys(a.entry.questions).length
                        : 0,
                      "a.questions": a.questions
                        ? Object.keys(a.questions).length
                        : 0,
                      "a.assessment?.questions": a.assessment?.questions
                        ? Object.keys(a.assessment.questions).length
                        : 0,
                      "assessmentDetails?.questions":
                        assessmentDetails?.questions
                          ? Object.keys(assessmentDetails.questions).length
                          : 0,
                      questionsToDisplay: questionsToDisplay
                        ? Object.keys(questionsToDisplay).length
                        : 0,
                      "a.entry keys": a.entry ? Object.keys(a.entry) : null,
                      "a.entry.questions sample": a.entry?.questions
                        ? Object.keys(a.entry.questions).slice(0, 3)
                        : null,
                    });
                  }

                  return (
                    <div key={idx}>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 120px 120px",
                          borderBottom: "1px solid #e2e8f0",
                          alignItems: "center",
                          cursor: "pointer",
                          background: isOpen ? "#f0f9ff" : "#ffffff",
                          transition: "all 0.2s ease",
                        }}
                        onClick={() => {
                          const sectionTitle =
                            a.sectionTitle || a.entry?.sectionTitle;
                          toggleExpandAssessment(
                            idx,
                            outcomeName,
                            sectionTitle || outlineTitle
                          );
                        }}
                        onMouseEnter={(e) => {
                          if (!isOpen)
                            e.currentTarget.style.background = "#f8fafc";
                        }}
                        onMouseLeave={(e) => {
                          if (!isOpen)
                            e.currentTarget.style.background = "#ffffff";
                        }}
                      >
                        <div
                          style={{
                            padding: "14px 8px",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                          }}
                        >
                          <span
                            style={{
                              transform: isOpen ? "rotate(90deg)" : "rotate(0)",
                              transition: "transform 0.2s",
                              color: isOpen ? "#2563eb" : "#64748b",
                              fontSize: "14px",
                              fontWeight: "bold",
                            }}
                          >
                            ▶
                          </span>
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "2px",
                            }}
                          >
                            <span style={{ fontWeight: 600, color: "#1e293b" }}>
                              {a.sectionTitle || "—"}
                            </span>
                            {!isOpen && (
                              <span
                                style={{ fontSize: "11px", color: "#64748b" }}
                              >
                                {
                                  Object.keys(
                                    a.questions ||
                                      a.entry?.questions ||
                                      a.assessment?.questions ||
                                      {}
                                  ).length
                                }{" "}
                                questions • Click to view answers
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          style={{
                            padding: "14px 8px",
                            textAlign: "right",
                            color: "#0f172a",
                            fontWeight: 600,
                          }}
                        >
                          <span
                            style={{
                              background: "#dbeafe",
                              padding: "4px 12px",
                              borderRadius: "6px",
                              color: "#1e40af",
                              border: "1px solid #bfdbfe",
                            }}
                          >
                            {typeof a.assessment?.percent === "number"
                              ? `${a.assessment.percent}%`
                              : typeof a.percent === "number"
                              ? `${a.percent}%`
                              : "—"}
                          </span>
                        </div>
                        <div
                          style={{
                            padding: "10px 8px",
                            textAlign: "right",
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div style={{ position: "relative" }}>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step={1}
                              defaultValue={
                                a.assessment?.percent ?? a.percent ?? 0
                              }
                              onChange={(e) =>
                                handleEditAssessmentPercent(
                                  idx,
                                  Number(e.target.value)
                                )
                              }
                              placeholder="0-100"
                              style={{
                                width: 90,
                                padding: "8px 28px 8px 12px",
                                border: "2px solid #3b82f6",
                                borderRadius: 8,
                                background: "#fff",
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#1e40af",
                              }}
                            />
                            <span
                              style={{
                                position: "absolute",
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "#64748b",
                                fontSize: "12px",
                                fontWeight: 600,
                                pointerEvents: "none",
                              }}
                            >
                              %
                            </span>
                          </div>
                        </div>
                      </div>
                      {isOpen && (
                        <div
                          style={{
                            padding: "20px",
                            background: "#ffffff",
                            borderBottom: "2px solid #e2e8f0",
                          }}
                        >
                          {/* Score Breakdown - Always Show */}
                          <div
                            style={{
                              background:
                                "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                              padding: "20px",
                              borderRadius: "12px",
                              border: "2px solid #3b82f6",
                              marginBottom: "20px",
                              boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: 700,
                                color: "#1e40af",
                                marginBottom: "16px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <span style={{ fontSize: "20px" }}>📊</span>
                              <span>Assessment Score Breakdown</span>
                            </div>
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3, 1fr)",
                                gap: "20px",
                                textAlign: "center",
                              }}
                            >
                              <div
                                style={{
                                  background: "#ffffff",
                                  padding: "16px",
                                  borderRadius: "8px",
                                  border: "1px solid #bfdbfe",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#64748b",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    marginBottom: "8px",
                                  }}
                                >
                                  Score Achieved
                                </div>
                                <div
                                  style={{
                                    fontSize: "32px",
                                    fontWeight: 800,
                                    color: "#3b82f6",
                                  }}
                                >
                                  {a.assessment?.score ?? "—"}
                                </div>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#94a3b8",
                                    marginTop: "4px",
                                  }}
                                >
                                  points
                                </div>
                              </div>
                              <div
                                style={{
                                  background: "#ffffff",
                                  padding: "16px",
                                  borderRadius: "8px",
                                  border: "1px solid #bfdbfe",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#64748b",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    marginBottom: "8px",
                                  }}
                                >
                                  Max Score
                                </div>
                                <div
                                  style={{
                                    fontSize: "32px",
                                    fontWeight: 800,
                                    color: "#1e293b",
                                  }}
                                >
                                  {a.assessment?.maxScore ?? "—"}
                                </div>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#94a3b8",
                                    marginTop: "4px",
                                  }}
                                >
                                  points
                                </div>
                              </div>
                              <div
                                style={{
                                  background: "#ffffff",
                                  padding: "16px",
                                  borderRadius: "8px",
                                  border: "1px solid #bfdbfe",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#64748b",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    marginBottom: "8px",
                                  }}
                                >
                                  Final Grade
                                </div>
                                <div
                                  style={{
                                    fontSize: "32px",
                                    fontWeight: 800,
                                    color: "#10b981",
                                  }}
                                >
                                  {typeof a.assessment?.percent === "number"
                                    ? `${a.assessment.percent}%`
                                    : "—"}
                                </div>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "#94a3b8",
                                    marginTop: "4px",
                                  }}
                                >
                                  percentage
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Questions and Answers */}
                          {isLoadingDetails && (
                            <div
                              style={{
                                padding: "16px",
                                textAlign: "center",
                                color: "#64748b",
                                fontSize: "13px",
                              }}
                            >
                              Loading assessment details...
                            </div>
                          )}
                          {!isLoadingDetails && assessmentDetails && (
                            <div
                              style={{
                                marginTop: "16px",
                                background: "#f8fafc",
                                padding: "16px",
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "14px",
                                  fontWeight: 700,
                                  color: "#1e40af",
                                  marginBottom: "12px",
                                }}
                              >
                                📝 Questions & Answers
                              </div>
                              {questionsToDisplay &&
                              Object.keys(questionsToDisplay).length > 0 ? (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "12px",
                                  }}
                                >
                                  {Object.entries(questionsToDisplay).map(
                                    ([questionId, questionData], qIdx) => (
                                      <div
                                        key={questionId}
                                        style={{
                                          background: "#ffffff",
                                          padding: "12px",
                                          borderRadius: "6px",
                                          border: "1px solid #e2e8f0",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "12px",
                                            fontWeight: 600,
                                            color: "#475569",
                                            marginBottom: "6px",
                                          }}
                                        >
                                          Question {qIdx + 1}:
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "13px",
                                            color: "#1e293b",
                                            marginBottom: "8px",
                                          }}
                                        >
                                          {questionData.question ||
                                            questionData.text ||
                                            questionId}
                                        </div>
                                        {questionData.answer !== undefined && (
                                          <div>
                                            <div
                                              style={{
                                                fontSize: "11px",
                                                fontWeight: 600,
                                                color: "#64748b",
                                                marginBottom: "4px",
                                              }}
                                            >
                                              Answer:
                                            </div>
                                            <div
                                              style={{
                                                fontSize: "12px",
                                                color: "#059669",
                                                background: "#d1fae5",
                                                padding: "6px 10px",
                                                borderRadius: "4px",
                                              }}
                                            >
                                              {typeof questionData.answer ===
                                              "object"
                                                ? JSON.stringify(
                                                    questionData.answer,
                                                    null,
                                                    2
                                                  )
                                                : String(questionData.answer)}
                                            </div>
                                          </div>
                                        )}
                                        {questionData.score !== undefined && (
                                          <div
                                            style={{
                                              fontSize: "11px",
                                              color: "#64748b",
                                              marginTop: "6px",
                                            }}
                                          >
                                            Score: {questionData.score} /{" "}
                                            {questionData.maxScore || "?"}
                                          </div>
                                        )}
                                      </div>
                                    )
                                  )}
                                </div>
                              ) : (
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#94a3b8",
                                    fontStyle: "italic",
                                  }}
                                >
                                  No questions found in assessment details.
                                </div>
                              )}
                            </div>
                          )}
                          {!isLoadingDetails &&
                            !assessmentDetails &&
                            cacheKey && (
                              <div
                                style={{
                                  marginTop: "16px",
                                  background: "#fef3c7",
                                  padding: "12px",
                                  borderRadius: "6px",
                                  border: "1px solid #fde68a",
                                  fontSize: "12px",
                                  color: "#92400e",
                                }}
                              >
                                Assessment details not available. Questions and
                                answers may not be stored for this assessment.
                              </div>
                            )}
                          {!isLoadingDetails && !cacheKey && (
                            <div
                              style={{
                                marginTop: "16px",
                                background: "#f8fafc",
                                padding: "16px",
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "13px",
                                  color: "#64748b",
                                  lineHeight: "1.6",
                                }}
                              >
                                <strong style={{ color: "#1e40af" }}>
                                  ℹ️ Note:
                                </strong>{" "}
                                This assessment was graded with an overall
                                score. Individual questions and answers are not
                                tracked in this format. You can adjust the final
                                grade percentage above if needed.
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div
              style={{
                padding: "16px 20px",
                borderTop: "2px solid #e2e8f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                background: "#f8fafc",
                flexShrink: 0,
              }}
            >
              <div
                style={{ fontSize: "13px", color: "#64748b", fontWeight: 500 }}
              >
                💡 Review answers and adjust grades as needed
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: "10px 20px",
                    borderRadius: 8,
                    border: "2px solid #e2e8f0",
                    background: "#fff",
                    color: "#475569",
                    cursor: "pointer",
                    fontWeight: 600,
                    fontSize: "14px",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = "#f8fafc";
                    e.target.style.borderColor = "#cbd5e1";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = "#fff";
                    e.target.style.borderColor = "#e2e8f0";
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveManualGrades}
                  disabled={savingGrades}
                  style={{
                    padding: "10px 24px",
                    borderRadius: 8,
                    border: 0,
                    background: savingGrades
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #3b82f6, #2563eb)",
                    color: "#fff",
                    cursor: savingGrades ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: "14px",
                    boxShadow: savingGrades
                      ? "none"
                      : "0 2px 8px rgba(37, 99, 235, 0.3)",
                    transition: "all 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!savingGrades) {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 4px 12px rgba(37, 99, 235, 0.4)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!savingGrades) {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 2px 8px rgba(37, 99, 235, 0.3)";
                    }
                  }}
                >
                  {savingGrades ? "Saving Changes..." : "💾 Save Changes"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function isUnitComplete(progressData) {
  if (!progressData || typeof progressData !== "object") return false;
  // Heuristics: Many places track outcome completion booleans in an object.
  // If all booleans inside are true, consider the unit completed.
  const values = Object.values(progressData);
  if (values.length === 0) return false;
  let anyObject = false;
  let allComplete = true;
  values.forEach((v) => {
    if (v && typeof v === "object") {
      anyObject = true;
      if (v.completed === false) allComplete = false;
      if (v.completed === true) return;
      if ("completed" in v === false) allComplete = false;
    }
  });
  return anyObject && allComplete;
}

// Build ordered units from full program structure for lock-walk
function buildOrderedUnitsFromProgram(programData) {
  if (!programData || !Array.isArray(programData.modules)) return [];
  const orderedModules = [...programData.modules].sort(
    (a, b) => (a.sequence || 0) - (b.sequence || 0)
  );
  const components = programData.components || [];
  const units = programData.units || [];
  const byComponentName = new Map(components.map((c) => [c.name, c]));
  const byUnitName = new Map(units.map((u) => [u.name, u]));

  const orderedUnits = [];
  for (const module of orderedModules) {
    const compNames = module.components || module.componentIds || [];
    for (const compName of compNames) {
      const comp = byComponentName.get(compName);
      if (!comp) continue;
      const unitNames = comp.units || comp.unitIds || [];
      for (const unitName of unitNames) {
        const unit = byUnitName.get(unitName);
        if (!unit) continue;
        const outcomes = Array.isArray(unit.outcomes)
          ? unit.outcomes.map((o) => ({ name: o.name }))
          : [];
        orderedUnits.push({ name: unit.name, outcomes });
      }
    }
  }
  return orderedUnits;
}

// Map normalized outcome title -> { unitName, outcomeName }
function buildOutcomeToUnitMap(programData) {
  const map = new Map();
  if (!programData || !Array.isArray(programData.units)) return map;
  const units = programData.units || [];
  units.forEach((unit) => {
    const outcomes = Array.isArray(unit.outcomes) ? unit.outcomes : [];
    outcomes.forEach((o) => {
      const key = normalizeTitle(o?.name || "");
      if (key) {
        map.set(key, { unitName: unit.name, outcomeName: o.name });
      }
    });
  });
  return map;
}

// Fallback matcher: try to match by removing punctuation and doing includes match
function tryLooseMatch(normalizedTitle, outcomeToUnit) {
  if (!normalizedTitle) return null;
  const loose = normalizedTitle.replace(/[^a-z0-9\s]/g, "").trim();
  for (const [key, value] of outcomeToUnit.entries()) {
    const k = key.replace(/[^a-z0-9\s]/g, "").trim();
    if (k === loose || k.includes(loose) || loose.includes(k)) {
      return value;
    }
  }
  return null;
}

// Check if an outcome ID exists and is completed across any module progress maps
function findOutcomeCompletedInModules(outcomeId, perModuleProgress) {
  if (!outcomeId || !perModuleProgress) return false;
  return Object.values(perModuleProgress).some((progressMap) => {
    return (
      progressMap &&
      progressMap[outcomeId] &&
      progressMap[outcomeId].completed === true
    );
  });
}

// Extract completed outcome names from learnerAssessmentStory doc
function extractCompletedOutcomeNamesFromStory(storyDoc, passPercent = 60) {
  if (!storyDoc || typeof storyDoc !== "object") return [];
  const names = new Set();
  // Expect arrays like entries/history where entry.sectionTitle (or outcomeName) indicates an outcome, and a flag/score signals completion
  const history =
    storyDoc.complexityHistory || storyDoc.history || storyDoc.entries || [];
  if (Array.isArray(history)) {
    history.forEach((entry) => {
      if (!entry) return;
      const sectionTitle =
        entry.sectionTitle || entry.outcomeName || entry.name;
      const assessment = entry.assessment || {};
      const percent = assessment.percent ?? entry.percent;
      const completed =
        assessment.completed === true ||
        (typeof percent === "number" && percent >= passPercent);
      if (sectionTitle && completed) names.add(normalizeTitle(sectionTitle));
    });
  }
  return Array.from(names);
}

function normalizeTitle(title) {
  if (!title || typeof title !== "string") return "";
  // Remove numeric prefixes like "1. ", trim, and collapse spaces; lowercase for comparison
  return title
    .replace(/^\s*\d+\.?\s*/, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

// Extract the most recent completed assessment's sectionTitle from learnerAssessmentStory
function extractLastCompletedOutcomeFromStory(storyDoc) {
  if (!storyDoc || typeof storyDoc !== "object") return null;
  const history =
    storyDoc.complexityHistory || storyDoc.history || storyDoc.entries || [];
  if (!Array.isArray(history) || history.length === 0) return null;
  let latest = null;
  let latestTs = -Infinity;
  history.forEach((entry) => {
    if (!entry) return;
    const title = entry.sectionTitle || entry.outcomeName || entry.name;
    if (!title) return;
    const ts = extractTimestampMs(entry.at || entry.updatedAt);
    if (ts > latestTs) {
      latestTs = ts;
      latest = title;
    }
  });
  return latest || null;
}
// Walk history from latest to oldest, return first outcome that maps to a program unit
function mapLatestMatchingStoryOutcomeToUnit(
  storyDoc,
  outcomeToUnit,
  orderedUnits
) {
  if (!storyDoc || typeof storyDoc !== "object") return null;
  const history =
    storyDoc.complexityHistory || storyDoc.history || storyDoc.entries || [];
  if (!Array.isArray(history) || history.length === 0) return null;
  // sort by time desc
  const withTs = history
    .map((e) => ({ e, ts: extractTimestampMs(e?.at || e?.updatedAt) }))
    .sort((a, b) => b.ts - a.ts);
  for (const { e } of withTs) {
    const rawTitle = e?.sectionTitle || e?.outcomeName || e?.name;
    if (!rawTitle) continue;
    const norm = normalizeTitle(rawTitle);
    const mapped =
      outcomeToUnit.get(norm) ||
      tryLooseMatch(norm, outcomeToUnit) ||
      tryTokenOverlap(norm, outcomeToUnit);
    if (mapped) {
      // ensure the unit exists in orderedUnits
      const exists = orderedUnits.some((u) => u.name === mapped.unitName);
      if (exists)
        return {
          unitName: mapped.unitName,
          outcomeName: mapped.outcomeName,
          sourceTitle: rawTitle,
        };
    }
  }
  return null;
}

// Token-overlap fallback: require >=2 shared tokens to consider a match
function tryTokenOverlap(normalizedTitle, outcomeToUnit) {
  if (!normalizedTitle) return null;
  const tokens = normalizedTitle.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return null;
  let best = null;
  let bestScore = 0;
  for (const [key, value] of outcomeToUnit.entries()) {
    const keyTokens = key.split(/\s+/).filter(Boolean);
    const overlap = keyTokens.filter((t) => tokens.includes(t));
    if (overlap.length > bestScore) {
      bestScore = overlap.length;
      best = value;
    }
  }
  return bestScore >= 2 ? best : null;
}

// Count completed assessments in learnerAssessmentStory
function countCompletedAssessmentsFromStory(storyDoc) {
  if (!storyDoc || typeof storyDoc !== "object") return 0;
  const history =
    storyDoc.complexityHistory || storyDoc.history || storyDoc.entries || [];
  if (!Array.isArray(history)) return 0;
  return history.length;
}

function computeAveragePercentFromStory(storyDoc) {
  if (!storyDoc || typeof storyDoc !== "object") return 0;
  const history =
    storyDoc.complexityHistory || storyDoc.history || storyDoc.entries || [];
  if (!Array.isArray(history) || history.length === 0) return 0;
  let total = 0;
  let count = 0;
  history.forEach((entry) => {
    const assessment = entry?.assessment || {};
    const pct =
      typeof assessment.percent === "number"
        ? assessment.percent
        : typeof entry?.percent === "number"
        ? entry.percent
        : null;
    if (typeof pct === "number") {
      total += pct;
      count += 1;
    }
  });
  if (count === 0) return 0;
  return Math.round((total / count) * 10) / 10; // one decimal
}

// ─────────────────────────────────────────────────────────────────────────────
// Assessment details fetching
// ─────────────────────────────────────────────────────────────────────────────
async function fetchAssessmentDetails(
  learnerEmail,
  unitId,
  outcomeId,
  outlineId
) {
  const db = getFirestore();

  console.log("[fetchAssessmentDetails] Attempting to fetch:", {
    learnerEmail,
    unitId,
    outcomeId,
    outlineId,
    path: `TPC_Group/TechPerformanceCentre/learnerAssessmentStory/${learnerEmail}/units/${unitId}/outcomes/${outcomeId}/outline/${outlineId}`,
  });

  // First, try to list all outline documents to see what IDs actually exist
  try {
    const outlineCollectionRef = collection(
      db,
      "TPC_Group",
      "TechPerformanceCentre",
      "learnerAssessmentStory",
      learnerEmail,
      "units",
      unitId,
      "outcomes",
      outcomeId,
      "outline"
    );

    console.log("[fetchAssessmentDetails] Listing outline documents...");
    const outlineSnaps = await getDocs(outlineCollectionRef);
    const existingIds = outlineSnaps.docs.map((doc) => doc.id);
    console.log(
      "[fetchAssessmentDetails] Found outline document IDs:",
      existingIds
    );

    // Try to find a match by comparing sectionTitle in the document data
    for (const docSnap of outlineSnaps.docs) {
      const data = docSnap.data();
      const docSectionTitle = data.sectionTitle || data.title || data.name;

      // Check if this document matches our outlineId (by sectionTitle)
      if (
        docSectionTitle &&
        (docSectionTitle === outlineId ||
          docSectionTitle.toLowerCase() === outlineId.toLowerCase() ||
          docSectionTitle.includes(outlineId) ||
          outlineId.includes(docSectionTitle))
      ) {
        console.log(
          "[fetchAssessmentDetails] Found matching document by sectionTitle:",
          {
            docId: docSnap.id,
            sectionTitle: docSectionTitle,
            requestedOutlineId: outlineId,
          }
        );
        return data;
      }
    }

    // If no match by sectionTitle, try exact ID match
    if (existingIds.includes(outlineId)) {
      const exactDoc = outlineSnaps.docs.find((d) => d.id === outlineId);
      if (exactDoc) {
        console.log(
          "[fetchAssessmentDetails] Found exact ID match:",
          outlineId
        );
        return exactDoc.data();
      }
    }
  } catch (listError) {
    console.warn(
      "[fetchAssessmentDetails] Error listing outline documents:",
      listError
    );
    // Fall through to try direct reads
  }

  // Fallback: Try multiple ID format variations with direct reads
  const idVariations = [
    { outcomeId, outlineId }, // Original (exact match - try this first!)
    { outcomeId, outlineId: outlineId.toLowerCase() }, // Original outcomeId, lowercase outlineId
    { outcomeId: outcomeId.toLowerCase(), outlineId: outlineId.toLowerCase() }, // Both lowercase
    {
      outcomeId,
      outlineId: outlineId.replace(/\s+/g, "-"),
    }, // Spaces to dashes in outlineId only
    {
      outcomeId: outcomeId.replace(/\s+/g, "-"),
      outlineId: outlineId.replace(/\s+/g, "-"),
    }, // Spaces to dashes in both
    {
      outcomeId: outcomeId.replace(/[^a-zA-Z0-9]/g, "-"),
      outlineId: outlineId.replace(/[^a-zA-Z0-9]/g, "-"),
    }, // Special chars to dashes
    {
      outcomeId: outcomeId
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, ""),
      outlineId: outlineId
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, ""),
    }, // Fully normalized
    // Try removing the number prefix (e.g., "2. " -> "")
    {
      outcomeId,
      outlineId: outlineId.replace(/^\d+\.\s*/, ""),
    },
    {
      outcomeId,
      outlineId: outlineId.replace(/^\d+\.\s*/, "").toLowerCase(),
    },
    {
      outcomeId,
      outlineId: outlineId
        .replace(/^\d+\.\s*/, "")
        .replace(/\s+/g, "-")
        .toLowerCase(),
    },
  ];

  for (const variation of idVariations) {
    try {
      const outlineRef = doc(
        db,
        "TPC_Group",
        "TechPerformanceCentre",
        "learnerAssessmentStory",
        learnerEmail,
        "units",
        unitId,
        "outcomes",
        variation.outcomeId,
        "outline",
        variation.outlineId
      );

      console.log("[fetchAssessmentDetails] Trying path:", outlineRef.path);
      const snap = await getDoc(outlineRef);

      if (snap.exists()) {
        const data = snap.data(); // Contains questions and answers
        console.log(
          "[fetchAssessmentDetails] Success! Data keys:",
          Object.keys(data || {})
        );
        return data;
      }
    } catch (error) {
      console.warn(
        "[fetchAssessmentDetails] Error trying variation:",
        variation,
        error
      );
      // Continue to next variation
    }
  }

  console.warn(
    "[fetchAssessmentDetails] No document found with any ID variation"
  );
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal helpers
// ─────────────────────────────────────────────────────────────────────────────
function extractAssessmentsArrayFromStory(storyDoc) {
  const history =
    storyDoc?.complexityHistory || storyDoc?.history || storyDoc?.entries || [];
  if (!Array.isArray(history)) return [];

  console.log("[extractAssessments] History entries:", history.length);
  if (history.length > 0) {
    console.log("[extractAssessments] First entry sample:", history[0]);
    console.log(
      "[extractAssessments] First entry assessment:",
      history[0]?.assessment
    );
    console.log(
      "[extractAssessments] First entry questions:",
      history[0]?.assessment?.questions
    );
  }

  return history.map((e, idx) => {
    // Try multiple locations for questions - they might be stored in different places
    // Based on the data structure, questions are at the root level of the entry
    let questions = null;

    // Check if questions exist and what format they're in
    // Questions are at the ROOT level of the entry (e.questions) as a map with numeric keys
    if (e?.questions) {
      // Questions might be an object (map) or array
      if (typeof e.questions === "object" && !Array.isArray(e.questions)) {
        const questionKeys = Object.keys(e.questions);
        if (questionKeys.length > 0) {
          questions = e.questions;
          if (idx < 3) {
            console.log(
              `[extractAssessments] Entry ${idx}: ✅ Found ${questionKeys.length} questions at e.questions`
            );
            console.log(
              `[extractAssessments] Entry ${idx}: Question keys:`,
              questionKeys.slice(0, 5)
            );
          }
        } else {
          if (idx < 3) {
            console.log(
              `[extractAssessments] Entry ${idx}: ⚠️ e.questions exists but is empty object`
            );
          }
        }
      } else if (Array.isArray(e.questions) && e.questions.length > 0) {
        // Convert array to object with numeric keys if needed
        questions = e.questions.reduce((acc, q, i) => {
          acc[i] = q;
          return acc;
        }, {});
        if (idx < 3) {
          console.log(
            `[extractAssessments] Entry ${idx}: Found ${e.questions.length} questions as array, converted to object`
          );
        }
      }
    } else {
      if (idx < 3) {
        console.log(
          `[extractAssessments] Entry ${idx}: ⚠️ e.questions is ${
            e?.questions === undefined
              ? "undefined"
              : e?.questions === null
              ? "null"
              : "falsy"
          }`
        );
      }
    }

    // Fallback to assessment.questions
    if (!questions && e?.assessment?.questions) {
      if (
        typeof e.assessment.questions === "object" &&
        !Array.isArray(e.assessment.questions)
      ) {
        questions =
          Object.keys(e.assessment.questions).length > 0
            ? e.assessment.questions
            : null;
      } else if (
        Array.isArray(e.assessment.questions) &&
        e.assessment.questions.length > 0
      ) {
        questions = e.assessment.questions.reduce((acc, q, i) => {
          acc[i] = q;
          return acc;
        }, {});
      }
    }

    // Default to empty object if no questions found
    questions = questions || {};

    if (idx === 0 || idx < 3) {
      console.log(`[extractAssessments] Processing entry ${idx}:`);
      console.log(
        "  - sectionTitle:",
        e?.sectionTitle || e?.outcomeName || e?.name
      );
      console.log("  - assessment:", e?.assessment);
      console.log("  - e.questions (direct):", e?.questions);
      console.log("  - e.assessment?.questions:", e?.assessment?.questions);
      console.log("  - questions object:", questions);
      console.log("  - questions keys:", Object.keys(questions));
      console.log("  - questions type:", typeof questions);
      console.log("  - Full entry keys:", Object.keys(e || {}));
      console.log(
        "  - Full entry (first 3):",
        JSON.stringify(e, null, 2).substring(0, 500)
      );
      // Log a sample question if it exists
      if (questions && Object.keys(questions).length > 0) {
        const firstQKey = Object.keys(questions)[0];
        console.log("  - Sample question:", questions[firstQKey]);
      } else {
        console.log("  - ⚠️ NO QUESTIONS FOUND in entry!");
        // Try to find questions in other locations
        console.log("  - Checking e keys:", Object.keys(e || {}));
        console.log(
          "  - e.questions type:",
          typeof e?.questions,
          "value:",
          e?.questions
        );
        console.log("  - e.assessment type:", typeof e?.assessment);
        if (e?.assessment) {
          console.log("  - e.assessment keys:", Object.keys(e.assessment));
          console.log(
            "  - e.assessment.questions type:",
            typeof e?.assessment?.questions,
            "value:",
            e?.assessment?.questions
          );
        }
        // Check if questions might be an array
        if (Array.isArray(e?.questions)) {
          console.log(
            "  - ⚠️ e.questions is an ARRAY, not object! Length:",
            e.questions.length
          );
        }
        if (Array.isArray(e?.assessment?.questions)) {
          console.log(
            "  - ⚠️ e.assessment.questions is an ARRAY, not object! Length:",
            e.assessment.questions.length
          );
        }
      }
    }

    // Ensure questions are properly stored - use the extracted questions or fallback to original
    const finalQuestions =
      questions && Object.keys(questions).length > 0
        ? questions
        : e?.questions && Object.keys(e.questions).length > 0
        ? e.questions
        : {};

    return {
      sectionTitle: e?.sectionTitle || e?.outcomeName || e?.name || "",
      percent:
        typeof e?.assessment?.percent === "number"
          ? e.assessment.percent
          : typeof e?.percent === "number"
          ? e.percent
          : null,
      assessment: {
        ...(e?.assessment || {}),
        questions: finalQuestions, // Store questions in assessment object
        maxScore: e?.assessment?.maxScore ?? e?.maxScore ?? null,
        score: e?.assessment?.score ?? e?.score ?? null,
      },
      // Store the full entry for reference - questions are here at root level
      entry: e,
      // Also store questions at root level for easy access - this is where they actually are!
      questions: finalQuestions,
      // Store original questions locations for debugging
      _originalQuestions: e?.questions,
      _originalAssessmentQuestions: e?.assessment?.questions,
    };
  });
}

function extractTimestampMs(at) {
  if (!at) return 0;
  try {
    if (typeof at === "string") {
      const d = new Date(at);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    }
    if (typeof at === "object") {
      // Firestore Timestamp-like
      if (typeof at.seconds === "number")
        return at.seconds * 1000 + (at.nanoseconds ? at.nanoseconds / 1e6 : 0);
      // Date object
      if (typeof at.getTime === "function") return at.getTime();
    }
  } catch (_e) {}
  return 0;
}

// Attempt to derive completed assessments from a "grades"-like doc
function extractCompletedAssessmentsFromGrades(gradesDoc) {
  if (!gradesDoc || typeof gradesDoc !== "object") return [];
  const results = [];
  const history = gradesDoc.complexityHistory || gradesDoc.history || [];
  if (Array.isArray(history)) {
    history.forEach((entry) => {
      if (!entry) return;
      const sectionTitle = entry.sectionTitle || entry.unit || entry.unitName;
      const assessment = entry.assessment || {};
      const percent = assessment.percent || entry.percent;
      const completed = assessment.completed === true || percent >= 0;
      if (completed) {
        results.push({
          unit: sectionTitle || null,
          percent: typeof percent === "number" ? percent : null,
          score: assessment.score || null,
          maxScore: assessment.maxScore || null,
          completedAt: entry.at || entry.updatedAt || null,
        });
      }
    });
  }
  return results;
}

function sanitizeProgramStructure(programData) {
  if (!programData || typeof programData !== "object") return null;
  return {
    id: programData.id || programData.name || "",
    name: programData.name || "",
    modules: Array.isArray(programData.modules) ? programData.modules : [],
    components: Array.isArray(programData.components)
      ? programData.components
      : [],
    units: Array.isArray(programData.units) ? programData.units : [],
  };
}

function buildUnitDocumentId(unitName, programName) {
  if (!unitName) return "";
  return `${unitName}|||${programName || "TPC_Program"}|||${COMPANY_CODE}`;
}
function buildComponentProgressTree(programData, storyDoc) {
  if (!programData || typeof programData !== "object") {
    return { components: [] };
  }

  const unitIndex = buildUnitComponentIndex(programData);
  const components = unitIndex.componentEntries;
  const outcomeToUnit = buildOutcomeToUnitMap(programData);
  const history =
    storyDoc?.complexityHistory || storyDoc?.history || storyDoc?.entries || [];

  if (Array.isArray(history)) {
    history.forEach((entry, historyIndex) => {
      const resolved = resolveHistoryEntryToUnit(
        entry,
        outcomeToUnit,
        unitIndex
      );
      if (!resolved) return;

      const { componentRef, unitRef, outcomeName } = resolved;
      const details = {
        historyIndex,
        sectionTitle:
          entry?.sectionTitle ||
          outcomeName ||
          `Assessment ${historyIndex + 1}`,
        outcomeName: outcomeName || entry?.outcomeName || entry?.sectionTitle,
        percent:
          typeof entry?.assessment?.percent === "number"
            ? entry.assessment.percent
            : typeof entry?.percent === "number"
            ? entry.percent
            : null,
        score:
          typeof entry?.assessment?.score === "number"
            ? entry.assessment.score
            : typeof entry?.score === "number"
            ? entry.score
            : null,
        maxScore:
          typeof entry?.assessment?.maxScore === "number"
            ? entry.assessment.maxScore
            : typeof entry?.maxScore === "number"
            ? entry.maxScore
            : null,
        completedAt: entry?.at || entry?.updatedAt || null,
      };

      unitRef.assessments.push(details);
      componentRef.assessmentsCount = (componentRef.assessmentsCount || 0) + 1;
    });
  }

  components.forEach((component) => {
    component.units.sort((a, b) => (a.order || 0) - (b.order || 0));
  });

  return { components };
}

function buildUnitComponentIndex(programData) {
  const orderedModules = Array.isArray(programData.modules)
    ? [...programData.modules].sort(
        (a, b) => (a?.sequence || 0) - (b?.sequence || 0)
      )
    : [];
  const componentsByName = new Map(
    (programData.components || []).map((component) => [
      component.name,
      component,
    ])
  );
  const unitsByName = new Map(
    (programData.units || []).map((unit) => [unit.name, unit])
  );

  const componentEntries = [];
  const unitLookup = new Map();

  orderedModules.forEach((module, moduleIdx) => {
    const moduleName = module?.name || `Module ${moduleIdx + 1}`;
    const moduleSequence = module?.sequence || moduleIdx + 1;
    const moduleComponents = Array.isArray(module?.components)
      ? module.components
      : [];

    moduleComponents.forEach((componentName, componentIdx) => {
      const componentData = componentsByName.get(componentName) || {
        name: componentName,
        units: [],
      };
      const componentKey = `${moduleName}|||${componentData.name}`;
      const unitEntries = (componentData.units || []).map(
        (unitName, unitIdx) => {
          const unitData = unitsByName.get(unitName) || { name: unitName };
          const unitEntry = {
            unitName: unitData.name || unitName,
            unitId: buildUnitDocumentId(
              unitData.name || unitName,
              programData.name
            ),
            order: unitData.sequence || unitIdx + 1,
            assessments: [],
          };
          unitLookup.set(normalizeTitle(unitEntry.unitName), {
            unitRef: unitEntry,
            componentRef: null,
          });
          return unitEntry;
        }
      );

      const componentEntry = {
        componentName: componentData.name,
        componentKey,
        moduleName,
        moduleSequence,
        componentSequence: componentData.sequence || componentIdx + 1,
        units: unitEntries,
        assessmentsCount: 0,
      };

      componentEntry.units.forEach((unit) => {
        const key = normalizeTitle(unit.unitName);
        unitLookup.set(key, {
          unitRef: unit,
          componentRef: componentEntry,
        });
      });

      componentEntries.push(componentEntry);
    });
  });

  componentEntries.sort((a, b) => {
    if ((a.moduleSequence || 0) !== (b.moduleSequence || 0)) {
      return (a.moduleSequence || 0) - (b.moduleSequence || 0);
    }
    return (a.componentSequence || 0) - (b.componentSequence || 0);
  });

  return { componentEntries, unitLookup };
}

function resolveHistoryEntryToUnit(entry, outcomeToUnit, unitIndex) {
  if (!entry) return null;
  const directUnit =
    entry.unitName || entry.unit || entry.unitTitle || entry.unitId;
  if (directUnit) {
    const direct = getUnitFromLookup(directUnit, unitIndex.unitLookup);
    if (direct) {
      return {
        ...direct,
        outcomeName: entry.outcomeName || entry.sectionTitle,
      };
    }
  }

  const rawOutcome =
    entry.outcomeName ||
    entry.assessment?.outcomeName ||
    entry.sectionTitle ||
    entry.name;
  const normalizedOutcome = normalizeTitle(rawOutcome);
  if (normalizedOutcome) {
    const mapped =
      outcomeToUnit.get(normalizedOutcome) ||
      tryLooseMatch(normalizedOutcome, outcomeToUnit) ||
      tryTokenOverlap(normalizedOutcome, outcomeToUnit);
    if (mapped) {
      const resolved = getUnitFromLookup(mapped.unitName, unitIndex.unitLookup);
      if (resolved) {
        return { ...resolved, outcomeName: mapped.outcomeName || rawOutcome };
      }
    }
  }

  if (entry.sectionTitle) {
    const fuzzy = fuzzyMatchUnit(entry.sectionTitle, unitIndex.unitLookup);
    if (fuzzy) {
      return { ...fuzzy, outcomeName: entry.sectionTitle };
    }
  }

  return null;
}

function getUnitFromLookup(unitName, lookup) {
  if (!unitName) return null;
  const key = normalizeTitle(unitName);
  return lookup.get(key) || null;
}

function fuzzyMatchUnit(sectionTitle, lookup) {
  const normalizedSection = normalizeTitle(sectionTitle);
  if (!normalizedSection) return null;

  let best = null;
  let bestScore = 0;
  lookup.forEach((value, key) => {
    const keyTokens = key.split(" ").filter(Boolean);
    const overlap = keyTokens.filter((token) =>
      normalizedSection.includes(token)
    ).length;
    if (overlap > bestScore) {
      bestScore = overlap;
      best = value;
    }
  });
  return bestScore >= 2 ? best : null;
}

function findUnitInProgramStructure(unitName, componentProgress) {
  if (!componentProgress || !Array.isArray(componentProgress.components)) {
    return null;
  }

  for (const component of componentProgress.components) {
    if (!Array.isArray(component.units)) continue;
    const unit = component.units.find(
      (u) => normalizeTitle(u.unitName) === normalizeTitle(unitName)
    );
    if (unit) {
      return unit;
    }
  }
  return null;
}
